import CryptoJS from 'crypto-js';
import JSEncrypt from 'jsencrypt';
import { ENV_TYPE, RSA_KEY, LOCAL_STORAGE_KEY } from '@/constants';
import DOMPurify from 'dompurify';
import { BRM_URL_HOST, ROADSHOW_URL_HOST } from '@/apis/host';
import { storage } from '@/utils/storageOperateApi';
import { isWorkBuddyClient, OPEN_FROM_WORKBUDDY } from '@/utils/embedEntry';
import slsLogger from '@/utils/slsLogger';
import electron, { isElectron } from './electron';
import { emitter } from '@/plugins/emitter';
import i18n from '@/plugins/i18n';
// 动态预热 markdown 相关库，将 markdown-it 与 KaTeX（renderer.js 内静态依赖 katex ~256KB）
// 一并移出初始 vendor bundle——applyKatexMath 经 _loadMdLibs 动态加载，避免 api.js
// 作为首屏强依赖把 katex 拖进首屏。
// 注意：公式渲染已统一改用 KaTeX（见 markdownPreview/renderer.js 的 applyKatexMath），
// 不再依赖 markdown-it-mathjax3——后者是 CJS 包，在浏览器侧动态 import 会抛
// `require is not defined`，进而让整个预热 Promise reject、正文渲染被跳过，切勿再加回。

let _mdLibs = null;
let _mdLibsPromise = null;
const _loadMdLibs = () => {
  if (_mdLibs) return Promise.resolve(_mdLibs);
  if (!_mdLibsPromise) {
    _mdLibsPromise = Promise.all([
      import('markdown-it'),
      import('@/components/markdownPreview/preProcessors'),
      // renderer.js 顶层静态依赖 katex（~256KB），随此动态 import 落入 async chunk，移出首屏
      import('@/components/markdownPreview/renderer'),
      import('@/components/markdownPreview/linkRule')
    ]).then(([{ default: markdownit }, preProcessors, renderer, linkRule]) => {
      _mdLibs = {
        markdownit,
        applyPreProcessors: preProcessors.applyPreProcessors,
        applyKatexMath: renderer.applyKatexMath,
        applyExternalLinkTargetBlank: linkRule.applyExternalLinkTargetBlank
      };
      _mdLibsPromise = null;
      return _mdLibs;
    });
  }
  return _mdLibsPromise;
};

export const getTransformToHtmlAsync = async (options = {}) => {
  await _loadMdLibs();
  return getTransformToHtml(options);
};

export {
  format,
  isToday,
  isTomorrow,
  isYesterday,
  isCurrentYear,
  getStartOfDay,
  getEndOfDay,
  dateFormat,
  dateFormaCustom,
  formatDuration,
  normalizeTimestamp,
  formatUpdateTime,
  formatTodayTomorrowTime,
  formatRelativeDate,
  formatRelativeDateTime,
  formatContentUpdatedAt,
  formatRelativeDateWithWeekday,
  formatCustomTime,
  formatReleaseDate,
  defaultShortcutsTime
} from '@/utils/date';
import { formatBigPriceByLocale, formatAllTotalByLocale, formatTotalByLocale } from '@/utils/formatter';
import { isEnLang as isEnLangFromLang } from '@/utils/lang';

/**
 * 是否在vite构建中
 */
export const inVite = () => {
  // __webpack_require__ 仅在 webpack bundle 中定义；
  // webpack 编译期会将此条件替换为 false 并 tree-shake 掉 Vite 分支，
  // 避免 import.meta.glob 在 webpack 运行时被调用。
  return typeof __webpack_require__ === 'undefined';
};
/**
 * 获取当前环境env下key的value值, webpack为process.env，vite为import.meta.env,vite自定义的环境变量注入客户端时变量需要加上VITE_（vue-cli的加上VUE_APP）
 */
export const getEnvInfoByKey = (envKey) => {
  let env = {};
  try {
    if (envKey && envKey.includes('VUE_APP')) {
      env = import.meta.env[`VITE_${envKey}`];
    } else {
      env = import.meta.env[`${envKey}`];
    }
  } catch {
    env = process.env[envKey];
  }
  return env;
};

/**
 * 判断是否为海外环境
 */
export const isOverseasEnv = () => {
  return ENV_DATA.VITE_APP_ENV.includes('overseas');
};
/**
 * 获取当前环境 生产 pro/ 测试 test/ 开发 local
 * 区分线上环境(build)/开发环境(local)/测试环境(test)
 */
export const getEnv = () => {
  let env = ENV_TYPE.LOCAL;
  if (ENV_DATA.VITE_APP_ENV.includes('production')) {
    env = ENV_TYPE.PRO;
  } else if (ENV_DATA.VITE_APP_ENV.includes('test')) {
    env = ENV_TYPE.TEST;
  }
  return env;
};

// 正则校验
//
// 匹配任意空或空白字符，如果你什么也没输入，或输入的只有空格、回车、换行等字符，则匹配成功
export const isBlank = (str) => {
  const reg = /^\s*$/;
  return reg.test(str);
};

// 正则校验。
// 邮箱格式：“名称@域名”，需要使用“^”匹配邮箱的开始部分，用“$”匹配邮箱结束部分以保证邮箱前后不能有其他字符
export const isEmail = (str) => {
  const reg = /^[A-Za-z\d]+([-_.][A-Za-z\d]+)*@([A-Za-z\d]+[-.])+[A-Za-z\d]{2,4}$/;
  return reg.test(str);
};

// 匹配是否是纯数字整数（不能含符号及字母）
export const isAllNum = (str) => {
  const reg = /^\d+$/;
  return reg.test(str);
};

// 匹配是否是6位纯数字整数（不能含符号及字母）
export const isSixAllNum = (str) => {
  const reg = /^\d{6}$/;
  return reg.test(str);
};

// 匹配 表情 字段
export const regEmoji = (str) => {
  const reg = /\[[A-Za-z]+]|\[[\u4e00-\u9fa5]+]/g;
  return str.match(reg);
};

// 清除 字符 前后空格
export const clearSpace = (str) => {
  return str.replace(/(^\s*)|(\s*)$/g, '');
};

// 十进制 转 二进制
export const Dec2Bin = (num) => {
  num = +num;
  return num.toString(2);
};

// 判断为空
//
// 是否为空
export const isEmpty = (key) => {
  return !key || typeof key === 'undefined' || key === null;
};

// 是否为空对象
export const isEmptyObject = (o) => {
  if (typeof o === 'object' && !(o instanceof Array) && o !== null) {
    for (const t in o) return !1;
    return !0;
  }
};

/**
 * 数组
 *
 * @param  { Array<Object> } arr
 * @return { Boolean }
 * **/
export const isEmptyArray = (arr) => !arr.length;

// 判断数据类型
//
// 是否为对象
export const isObject = (obj) => {
  return Object.prototype.toString.call(obj) === '[object Object]';
};

// 是否为 function
export const isFun = (fun) => {
  return Object.prototype.toString.call(fun) === '[object Function]';
};

// 是否为 array
export const isArr = (arr) => {
  return Object.prototype.toString.call(arr) === '[object Array]';
};

// 参数序列化
export const serialize = (serializeData) => {
  if (serializeData === undefined || serializeData === null || serializeData === '') {
    return '';
  } else {
    const params = [];
    for (let name in serializeData) {
      // 遍历对象属性
      if (!serializeData.hasOwnProperty(name)) continue; // 过滤掉继承的属性和方法
      if (typeof serializeData[name] === 'function') continue; // 过滤掉方法
      let value = serializeData[name].toString();
      name = encodeURIComponent(name);
      value = encodeURIComponent(value);
      params.push(name + '=' + value); // 属性名和属性值放入数组
    }
    return params.join('&');
  }
};

/**
 * 根据key值匹配对应的value值,适用场景例如：根据后端状态码，前端展示不同字段
 * @param k = 要匹配的key值
 * @param obj = 要进行筛选匹配的数据源
 * @returns {*}
 */
export const returnValueAccordingCode = (k, obj) => {
  if (typeof k === 'number') {
    for (const o in obj) {
      if (Number(o) === k) return obj[o];
    }
  } else {
    for (const o in obj) {
      if (o === k) return obj[o];
    }
  }
};

/**
 * 遍历数组 筛选出匹配满足条件的对象
 * @array: 需要遍历的数组
 * @obj: 过滤条件，以对象形式传（例{a:1,b:2}）
 * **/
export const traversalObject = (array, obj) => {
  const keys = Object.keys(obj);
  return array.filter((m) => {
    return keys.every((key) => m.hasOwnProperty(key) && m[key] === obj[key]);
  });
};

/**
 * 倒计时计算年，天，时，分，秒
 * @param targetTime {Number/String} 目标时间（将来的设定时间）
 * @param startTime {Number/String} 开始时间（一般为现在时间）
 * @return {{years: *, days: *, hours: *, minutes: *, seconds: *}} 返回对象
 * */
export const returnCountDownTime = (targetTime, startTime, length) => {
  // 格式化时间加0
  const zero = (num) => {
    return Number(num) < 10 ? '0' + num : String(num);
  };

  const currTime = startTime || new Date().getTime();
  const leftTime = length ? Number(length) : Number(targetTime) - Number(currTime);
  if (leftTime >= 0) {
    const years = parseInt(leftTime / 1000 / 60 / 60 / 24 / 365, 10); // //计算剩余的年
    const days = parseInt(leftTime / 1000 / 60 / 60 / 24, 10); // 计算剩余的天数
    const hours = parseInt((leftTime / 1000 / 60 / 60) % 24, 10); // 计算剩余的小时
    const minutes = parseInt((leftTime / 1000 / 60) % 60, 10); // 计算剩余的分钟
    const seconds = parseInt((leftTime / 1000) % 60, 10); // 计算剩余的秒数

    return {
      years: zero(years),
      days: zero(days),
      hours: zero(hours),
      minutes: zero(minutes),
      seconds: zero(seconds)
    };
  } else {
    return false;
  }
};

/**
 * 获取style样式
 */
export const getStyle = (element, attr, NumberMode = 'int') => {
  if (!element) return;
  let target;
  // scrollTop获取方式不同，没有它不属于style，而且只有document.body才能用
  if (attr === 'scrollTop') {
    target = element.scrollTop;
  } else if (element.currentStyle) {
    target = element.currentStyle[attr];
  } else {
    target = document.defaultView.getComputedStyle(element, null)[attr];
  }
  // 在获取opacity 时需要获取小数parseFloat
  return NumberMode === 'float' ? parseFloat(target) : parseInt(target);
};

/**
 * 运动效果
 * @param {HTMLElement} element   运动对象，必选
 * @param {JSON}        target    属性：目标值，必选
 * @param {number}      duration  运动时间，可选
 * @param {string}      mode      运动模式，可选
 * @param {function}    callback  可选，回调函数，链式动画
 */
export const animateEffect = (element, target, duration = 400, mode = 'ease-out', callback) => {
  if (element && element.timer) clearInterval(element.timer);

  // 判断不同参数的情况
  if (duration instanceof Function) {
    callback = duration;
    duration = 400;
  } else if (duration instanceof String) {
    mode = duration;
    duration = 400;
  }

  // 判断不同参数的情况
  if (mode instanceof Function) {
    callback = mode;
    mode = 'ease-out';
  }

  // 获取dom样式
  const attrStyle = (attr) => {
    if (attr === 'opacity') {
      return Math.round(getStyle(element, attr, 'float') * 100);
    } else {
      return getStyle(element, attr);
    }
  };
  // 根字体大小，需要从此将 rem 改成 px 进行运算
  const rootSize = parseFloat(document.documentElement.style.fontSize);

  const unit = {};
  const initState = {};

  // 获取目标属性单位和初始样式值
  Object.keys(target).forEach((attr) => {
    if (/[^\d^\.]+/gi.test(target[attr])) {
      unit[attr] = target[attr].match(/[^\d^\.]+/gi)[0] || 'px';
    } else {
      unit[attr] = 'px';
    }
    initState[attr] = attrStyle(attr);
  });

  // 去掉传入的后缀单位
  Object.keys(target).forEach((attr) => {
    if (unit[attr] == 'rem') {
      target[attr] = Math.ceil(parseInt(target[attr]) * rootSize);
    } else {
      target[attr] = parseInt(target[attr]);
    }
  });

  let flag = true; // 假设所有运动到达终点
  const remberSpeed = {}; // 记录上一个速度值,在ease-in模式下需要用到
  element.timer = setInterval(() => {
    Object.keys(target).forEach((attr) => {
      let iSpeed = 0; // 步长
      let status = false; // 是否仍需运动
      const iCurrent = attrStyle(attr) || 0; // 当前元素属性址
      let speedBase = 0; // 目标点需要减去的基础值，三种运动状态的值都不同
      let intervalTime; // 将目标值分为多少步执行，数值越大，步长越小，运动时间越长
      switch (mode) {
        case 'ease-out':
          speedBase = iCurrent;
          intervalTime = (duration * 5) / 400;
          break;
        case 'linear':
          speedBase = initState[attr];
          intervalTime = (duration * 20) / 400;
          break;
        case 'ease-in':
          const oldspeed = remberSpeed[attr] || 0;
          iSpeed = oldspeed + (target[attr] - initState[attr]) / duration;
          remberSpeed[attr] = iSpeed;
          break;
        default:
          speedBase = iCurrent;
          intervalTime = (duration * 5) / 400;
      }
      if (mode !== 'ease-in') {
        iSpeed = (target[attr] - speedBase) / intervalTime;
        iSpeed = iSpeed > 0 ? Math.ceil(iSpeed) : Math.floor(iSpeed);
      }
      // 判断是否达步长之内的误差距离，如果到达说明到达目标点
      switch (mode) {
        case 'ease-out':
          status = iCurrent != target[attr];
          break;
        case 'linear':
          status = Math.abs(Math.abs(iCurrent) - Math.abs(target[attr])) > Math.abs(iSpeed);
          break;
        case 'ease-in':
          status = Math.abs(Math.abs(iCurrent) - Math.abs(target[attr])) > Math.abs(iSpeed);
          break;
        default:
          status = iCurrent != target[attr];
      }

      if (status) {
        flag = false;
        // opacity 和 scrollTop 需要特殊处理
        if (attr === 'opacity') {
          element.style.filter = 'alpha(opacity:' + (iCurrent + iSpeed) + ')';
          element.style.opacity = (iCurrent + iSpeed) / 100;
        } else if (attr === 'scrollTop') {
          element.scrollTop = iCurrent + iSpeed;
        } else {
          element.style[attr] = iCurrent + iSpeed + 'px';
        }
      } else {
        flag = true;
      }

      if (flag) {
        clearInterval(element.timer);
        if (callback) {
          callback();
        }
      }
    });
  }, 20);
};

// Base64_encode
export const Base64_encode = (stringToEncode) => {
  const encodeUTF8string = function (str) {
    return encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function toSolidBytes(match, p1) {
      return String.fromCharCode('0x' + p1);
    });
  };
  if (typeof window !== 'undefined') {
    if (typeof window.btoa !== 'undefined') {
      window.btoa(encodeUTF8string(stringToEncode));
    }
  } else {
    return new Buffer(stringToEncode).toString('base64');
  }
  const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
  let o1;
  let o2;
  let o3;
  let h1;
  let h2;
  let h3;
  let h4;
  let bits;
  let i = 0;
  let ac = 0;
  let enc = '';
  const tmpArr = [];
  if (!stringToEncode) {
    return stringToEncode;
  }
  stringToEncode = encodeUTF8string(stringToEncode);
  do {
    o1 = stringToEncode.charCodeAt(i++);
    o2 = stringToEncode.charCodeAt(i++);
    o3 = stringToEncode.charCodeAt(i++);
    bits = (o1 << 16) | (o2 << 8) | o3;
    h1 = (bits >> 18) & 0x3f;
    h2 = (bits >> 12) & 0x3f;
    h3 = (bits >> 6) & 0x3f;
    h4 = bits & 0x3f;
    tmpArr[ac++] = B64.charAt(h1) + B64.charAt(h2) + B64.charAt(h3) + B64.charAt(h4);
  } while (i < stringToEncode.length);
  enc = tmpArr.join('');
  const r = stringToEncode.length % 3;
  return (r ? enc.slice(0, r - 3) : enc) + '==='.slice(r || 3);
};

// Base64_decode
export const Base64_decode = (encodedData) => {
  const decodeUTF8string = function (str) {
    return decodeURIComponent(
      str
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
  };
  if (typeof window !== 'undefined') {
    if (typeof window.atob !== 'undefined') {
      return decodeUTF8string(window.atob(encodedData));
    }
  } else {
    return new Buffer(encodedData, 'base64').toString('utf-8');
  }
  const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
  let o1;
  let o2;
  let o3;
  let h1;
  let h2;
  let h3;
  let h4;
  let bits;
  let i = 0;
  let ac = 0;
  let dec = '';
  const tmpArr = [];
  if (!encodedData) {
    return encodedData;
  }
  encodedData += '';
  do {
    // unpack four hexets into three octets using index points in b64
    h1 = B64.indexOf(encodedData.charAt(i++));
    h2 = B64.indexOf(encodedData.charAt(i++));
    h3 = B64.indexOf(encodedData.charAt(i++));
    h4 = B64.indexOf(encodedData.charAt(i++));
    bits = (h1 << 18) | (h2 << 12) | (h3 << 6) | h4;
    o1 = (bits >> 16) & 0xff;
    o2 = (bits >> 8) & 0xff;
    o3 = bits & 0xff;
    if (h3 === 64) {
      tmpArr[ac++] = String.fromCharCode(o1);
    } else if (h4 === 64) {
      tmpArr[ac++] = String.fromCharCode(o1, o2);
    } else {
      tmpArr[ac++] = String.fromCharCode(o1, o2, o3);
    }
  } while (i < encodedData.length);
  dec = tmpArr.join('');
  return decodeUTF8string(dec.replace(/\0+$/, ''));
};

// 判断浏览器版本
export const browser_type = () => {
  // 取得浏览器的userAgent字符串
  const userAgent = navigator.userAgent;
  // 判断是否Opera浏览器
  if (userAgent.indexOf('Opera') > -1) {
    return 'Opera';
  } else if (userAgent.indexOf('Firefox') > -1) {
    // 判断是否Firefox浏览器
    return 'Firefox';
  } else if (userAgent.indexOf('Edg') > -1) {
    return 'Edge';
  } else if (userAgent.indexOf('Chrome') > -1 || userAgent.indexOf('CriOS') > -1) {
    return 'Chrome';
  } else if (userAgent.indexOf('Safari') > -1) {
    // 判断是否Safari浏览器
    return 'Safari';
  } else if (userAgent.includes('compatible') && userAgent.includes('MSIE') && userAgent.includes('Opera')) {
    // 判断是否IE浏览器
    return 'IE';
  } else {
    return null;
  }
};

// 获取浏览器信息
export const getBrowserInfo = () => {
  const info = {};
  const ua = navigator.userAgent.toLowerCase();
  let startIndex, endIndex;

  // 火狐浏览器 "Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:74.0) Gecko/20100101 Firefox/74.0"
  // edge浏览器 "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.36 Edge/16.16299"
  // ie浏览器 "Mozilla/5.0 (Windows NT 10.0; WOW64; Trident/7.0; .NET4.0C; .NET4.0E; InfoPath.3; rv:11.0) like Gecko"
  // 360极速模式 "Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/69.0.3497.100 Safari/537.36 QIHU 360EE"
  // 360安全浏览器 "Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/78.0.3904.108 Safari/537.36 QIHU 360SE"
  // 360兼容模式 "Mozilla/5.0 (Windows NT 10.0; WOW64; Trident/7.0; .NET4.0C; .NET4.0E; InfoPath.3; rv:11.0) like Gecko"
  // 360浏览器在非360的网址下 "Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/78.0.3904.108 Safari/537.36"
  // QQ浏览器 Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/70.0.3538.25 Safari/537.36 Core/1.70.3756.400 QQBrowser/10.5.4039.400
  // 微信浏览器 Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/53.0.2785.116 Safari/537.36 QBCore/4.0.1295.400 QQBrowser/9.0.2524.400
  //    Mozilla/5.0 (Windows NT 6.1; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/53.0.2875.116 Safari/537.36 NetType/WIFI MicroMessenger/7.0.5 WindowsWechat
  // 谷歌浏览器 Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/80.0.3987.163 Safari/537.36

  if (ua.indexOf('firefox') != -1) {
    info.name = 'firefox';

    startIndex = ua.indexOf('firefox');
    info.version = ua.substring(startIndex).split('/')[1];
  }

  if (ua.indexOf('edge') != -1) {
    info.name = 'edge';

    startIndex = ua.indexOf('edge');
    info.version = ua.substring(startIndex).split('/')[1];
  }

  if (ua.indexOf('net') != -1 && ua.indexOf('rv') != -1) {
    info.name = 'ie';

    startIndex = ua.indexOf('rv');
    endIndex = ua.indexOf(')');

    info.version = ua.substring(startIndex, endIndex).split(':')[1];
  }

  if (ua.indexOf('qqbrowser') != -1 && ua.indexOf('micromessenger') != -1) {
    info.name = 'wechat';

    startIndex = ua.indexOf('chrome');
    endIndex = ua.indexOf('safari');
    info.version = ua.substring(startIndex, endIndex).split('/')[1];
  }

  if (ua.indexOf('qqbrowser') != -1 && ua.indexOf('micromessenger') < 0) {
    info.name = 'qqbrowser';

    startIndex = ua.indexOf('chrome');
    endIndex = ua.indexOf('safari');
    info.version = ua.substring(startIndex, endIndex).split('/')[1];
  }

  if (ua.indexOf('chrome') != -1 && ua.indexOf('edge') < 0 && ua.indexOf('qqbrowser') < 0) {
    info.name = 'chrome';

    startIndex = ua.indexOf('chrome');
    endIndex = ua.indexOf('safari');
    info.version = ua.substring(startIndex, endIndex).split('/')[1];
  }

  if (ua.indexOf('safari') != -1 && ua.indexOf('version') != -1) {
    info.name = 'safari';

    startIndex = ua.indexOf('version');
    endIndex = ua.indexOf('safari');
    info.version = ua.substring(startIndex, endIndex).split('/')[1];
  }

  if (ua.indexOf('windows') != -1) {
    info.os = 'win';

    if (ua.indexOf('windows nt 6.1')) {
      info.sys = '7.0';
    }
    if (ua.indexOf('windows nt 6.2')) {
      info.sys = '8.0';
    }
    if (ua.indexOf('windows nt 6.3')) {
      info.sys = '8.1';
    }
    if (ua.indexOf('windows nt 10.0')) {
      info.sys = '10.0';
    }
  } else if (ua.indexOf('mac') != -1) {
    info.os = 'mac';
  } else {
    info.os = 'linux';
  }

  // var arr = ua.match(/(msie|firefox|chrome|opera|version).*?([\d.]+)/);

  // info.name = arr[1].replace(/version/, "safari");
  // info.version = arr[2];
  info.version_num = Number(info.version ? info.version.split('.')[0] : 0);

  return info;
};

// export const getSystemInfo = () => {
//   var osData = [
//     { name: 'Windows 2000', group: 'windows_server', identifier: 'Windows NT 5.0', version: '5.0' },
//     { name: 'Windows XP', group: 'windows', identifier: 'Windows NT 5.1', version: '5.1' },
//     { name: 'Windows 2003', group: 'windows_server', identifier: 'Windows NT 5.2', version: '5.2' },
//     { name: 'Windows Vista', group: 'windows', identifier: 'Windows NT 6.0', version: '6.0' },
//     { name: 'Windows 7', group: 'windows', identifier: 'Windows NT 6.1', version: '7.0' },
//     { name: 'Windows 8', group: 'windows', identifier: 'Windows NT 6.2', version: '8.0' },
//     { name: 'Windows 8.1', group: 'windows', identifier: 'Windows NT 6.3', version: '8.1' },
//     { name: 'Windows 10', group: 'windows', identifier: 'Windows NT 10.0', version: '10.0' },
//     { name: 'Windows 2008', group: 'windows_server', identifier: 'Windows NT 6.0; WOW64', version: '6.0' },
//     { name: 'Windows 2008', group: 'windows_server', identifier: 'Windows NT 6.1; WOW64', version: '6.1' },
//     { name: 'Windows 2012', group: 'windows_server', identifier: 'Windows NT 6.3; Win64', version: '6.3' },
//     { name: 'Chrome OS', group: 'windows', identifier: 'CrOS' },
//     { name: 'Mac OS X Capitan', group: 'mac', identifier: 'Mac OS X (10([_|\.])11([0-9_\.]*))', versionSeparator: '[_|\.]' },
//     { name: 'Mac OS X Yosemite', group: 'mac', identifier: 'Mac OS X (10([_|\.])10([0-9_\.]*))', versionSeparator: '[_|\.]' },
//     { name: 'Mac OS X Mavericks', group: 'mac', identifier: 'Mac OS X (10([_|\.])9([0-9_\.]*))', versionSeparator: '[_|\.]' },
//     { name: 'Mac OS X Mountain Lion', group: 'mac', identifier: 'Mac OS X (10([_|\.])8([0-9_\.]*))', versionSeparator: '[_|\.]' },
//     { name: 'Mac OS X Lion', group: 'mac', identifier: 'Mac OS X (10([_|\.])7([0-9_\.]*))', versionSeparator: '[_|\.]' },
//     { name: 'Mac OS X Snow Leopard', group: 'mac', identifier: 'Mac OS X (10([_|\.])6([0-9_\.]*))', versionSeparator: '[_|\.]' },
//     { name: 'Mac OS X Leopard', group: 'mac', identifier: 'Mac OS X (10([_|\.])5([0-9_\.]*))', versionSeparator: '[_|\.]' },
//     { name: 'Mac OS X Tiger', group: 'mac', identifier: 'Mac OS X (10([_|\.])4([0-9_\.]*))', versionSeparator: '[_|\.]' },
//     { name: 'Mac OS X Panther', group: 'mac', identifier: 'Mac OS X (10([_|\.])3([0-9_\.]*))', versionSeparator: '[_|\.]' },
//     { name: 'Mac OS X Jaguar', group: 'mac', identifier: 'Mac OS X (10([_|\.])2([0-9_\.]*))', versionSeparator: '[_|\.]' },
//     { name: 'Mac OS X Puma', group: 'mac', identifier: 'Mac OS X (10([_|\.])1([0-9_\.]*))', versionSeparator: '[_|\.]' },
//     { name: 'Mac OS X Cheetah', group: 'mac', identifier: 'Mac OS X (10([_|\.])0([0-9_\.]*))', versionSeparator: '[_|\.]' },
//     { name: 'Mac OS', group: 'mac', identifier: 'Mac OS' },
//     { name: 'Ubuntu', group: 'linux_server', identifier: 'Ubuntu', versionIdentifier: 'Ubuntu/([0-9\.]*)' },
//     { name: 'CentOs', group: 'linux_server', identifier: 'CentOs', versionIdentifier: 'CentOs/([0-9\.]*)' },
//     { name: 'Debian', group: 'linux_server', identifier: 'Debian' },
//     { name: 'Gentoo', group: 'linux_server', identifier: 'Gentoo' },
//     { name: '国产系统', group: 'linux', identifier: 'Linux' }
//   ];
// }

/**
 * 下载文件流，以excel方式导出
 * @param blob 文件流数据
 * @param fileName 文件名
 * */
export const downloadFileByBlob = (blob, fileName = '文件', callback) => {
  if (typeof blob == 'object' && blob instanceof Blob) {
    // 如果是blob流
    if (window.navigator.msSaveOrOpenBlob) {
      navigator.msSaveBlob(blob, fileName);
    } else {
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(link.href);
    }
    callback && callback();
  } else {
    const link = document.createElement('a');
    link.href = decodeURIComponent(blob);
    setTimeout(() => {
      callback && callback();
      link.click();
    }, 100);
  }
};
export const electronDownload = (blob, fileName) => {
  const link = document.createElement('a');
  fileName = fileName.replace(/[\\/:*?"<>|]/g, '_');
  if (typeof blob == 'object' && blob instanceof Blob) {
    link.href = window.URL.createObjectURL(blob);
    const savePath = electron.sendSync('saveFileDialog', { filename: fileName });
    if (savePath) {
      return electron.send('downloadFile', {
        event: 'start',
        data: {
          savePath: savePath,
          url: link.href
        }
      });
    }
  } else {
    const name = decodeURIComponent(blob).split('/');
    name[name.length - 1] = name[name.length - 1].replace(/[:]/g, '_');
    link.href = decodeURIComponent(blob);
    const savePath = electron.sendSync('saveFileDialog', { filename: name[name.length - 1] });
    if (savePath) {
      return electron.send('downloadFile', {
        event: 'start',
        data: {
          savePath: savePath.includes('.') ? savePath : `${savePath}.${fileName.split('.').pop()}`,
          url: link.href
        }
      });
    }
  }
};

/**
 * 比较两个数组是否相同
 * @param resArr  原数组
 * @param comArr  比较数组
 * @returns Boolean
 * */
export const contrastResToArr = (resArr, comArr) => {
  if (Array.isArray(resArr) && Array.isArray(comArr) && resArr.length && resArr.length >= comArr.length) {
    for (let i = 0; i < comArr.length; i++) {
      let flag = false;
      for (let j = 0; j < resArr.length; j++) {
        if (resArr[j] == comArr[i]) {
          flag = true;
          break;
        }
      }
      if (flag == false) {
        return flag;
      }
    }
    return true;
  }
  return false;
};

/**
 *  将数组按照n个元素分组
 *  @param resArr 原数组
 *  @param num 每个数组元素个数
 *  @returns newArr 新数组
 * */
export const renderNewArrToNumber = (res, num) => {
  const result = [];
  if (res.length) {
    for (let i = 0; i < res.length; i += num) {
      result.push(res.slice(i, i + num));
    }
  }
  return result;
};

/**
 * 密码输入框正则表达式
 * 非中文 6-20位数
 * @param { String } pwd
 */
export const checkPwd = (pwd) => /^[^\u4e00-\u9fa5]{0,20}$/.test(pwd);

/**
 * 密码输入框正则表达式
 * 6-20位数包含字母和数字不含特殊字符
 * @param { String } pwd
 */
export const newCheckPwd = (pwd) => /^(?=.*[A-Za-z])(?=.*\d)[\s\S]{6,20}$/.test(pwd);

/**
 * 验证手机号
 *
 * @param  { String } phone
 * @return { Boolean }
 * **/
export const checkPhone = (phone) =>
  /^(13[0-9]|14[01456879]|15[0-35-9]|16[2567]|17[0-8]|18[0-9]|19[0-35-9])\d{8}$/.test(phone) || /^8\d+$/.test(phone);
// export const checkPhone = (phone) => /^[0-9]{6,50}$/.test(phone);

/**
 * 将多维数组变成一维数组
 * 使用递归的方法
 * arr: 传入的多维数组
 * res=[] 新数组，
 * param 参数
 */
export const flatten = (arr, initArr, param) => {
  const newArr = initArr;
  arr.forEach((item) => {
    if (item.children) {
      flatten(item.children, newArr, param);
    } else if (item[param]) {
      newArr.push(item[param]);
    }
  });
  return newArr;
};

//　获取麦克风设备
export const getAudioInput = async () => {
  return (await navigator.mediaDevices.enumerateDevices()).filter(
    (item) => item.kind === 'audioinput' && item.deviceId !== 'default' && item.deviceId !== 'communications'
  );
};

//　获取麦克风设备
export const getAudioInputAgora = (client) => {
  return new Promise((resolve) => {
    client &&
      client.getRecordingDevices((devices) => {
        devices =
          devices.filter(
            (item) => item.kind === 'audioinput' && item.deviceId !== 'default' && item.deviceId !== 'communications'
          ) || [];
        resolve(devices);
      });
  });
};

// 显示麦克风音浪变化
// @params deviceId：设备id
export const onShowMicVolumnWave = (deviceId, cb) => {
  return new Promise((resolve, reject) => {
    navigator.mediaDevices
      .getUserMedia({
        audio: {
          mandatory: {
            chromeMediaSourceId: deviceId
          }
        },
        video: false
      })
      .then((stream) => {
        const track = stream.getTracks()[0];
        beginDetect(stream, track);
      })
      .catch((err) => {
        console.log('麦克风音浪异常情况处理,MIC异常', err);
        reject(err);
      });

    // 麦克风音量检测
    function beginDetect(stream, track) {
      const audioCtx = window.AudioContext || window.webkitAudioContext;
      const audioContext = new audioCtx();
      // 将麦克风的声音输入这个对象
      // 创建一个MediaStreamAudioSourceNode接口来关联可能来自本地计算机麦克风或其他来源的音频流MediaStream
      const mediaStreamSource = audioContext.createMediaStreamSource(stream);
      // 创建一个音频分析对象，采样的缓冲区大小为4096，输入和输出都是单声道
      const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);
      // 将该分析对象与麦克风音频进行连接
      mediaStreamSource.connect(scriptProcessor);
      // 此举无甚效果，仅仅是因为解决 Chrome 自身的 bug
      scriptProcessor.connect(audioContext.destination);
      // 开始处理音频
      scriptProcessor.onaudioprocess = (e) => {
        // 获得缓冲区的输入音频，转换为包含了PCM通道数据的32位浮点数组
        const buffer = e.inputBuffer.getChannelData(0);
        // 获取缓冲区中最大的音量值
        const maxVal = Math.max(...buffer);
        // 显示音量值
        const mv = Math.round(maxVal * 50);
        cb && cb(mv);
      };
      const processorDisconnect = () => {
        scriptProcessor.disconnect(audioContext.destination);
      };
      const switchTrack = () => {
        track.stop();
      };
      // 返回scriptProcessor事件关闭方法
      resolve({
        processorDisconnect,
        switchTrack
      });
    }
  });
};

/*
 * 合并地址参数
 * param 支持
 * 1、id=10086&comein_channel=zhongxinzq
 * 2、{id: 10086, comein_channel: 'zhongxinzq'}
 */
export const mergeUrl = (url, param) => {
  if (!param) {
    return url;
  }

  const query = {};
  const urls = url.split('?');
  const res = urls[0];

  const getValue = (str) => {
    str.split('&').forEach((str) => {
      const index = str.indexOf('=');
      if (index > 0) {
        query[str.substring(0, index)] = str.substring(index + 1);
      }
    });
  };

  if (urls.length > 1 && urls[1]) {
    getValue(urls[1]);
  }

  if (typeof param === 'string') {
    getValue(param);
  } else {
    Object.assign(query, param);
  }

  const arrQuery = [];

  for (const key in query) {
    const value = query[key];
    if (value === undefined || value === null || value === '' || value === 'undefined' || value === 'null') {
      continue;
    }
    arrQuery.push(key + '=' + query[key]);
  }

  return res + (arrQuery.length > 0 ? '?' + arrQuery.join('&') : '');
};

export const parseChatItem = function (mess) {
  // 自定义消息体
  const new_mess = {};
  new_mess.id = mess._id;
  new_mess.from = mess._from;
  new_mess.to = mess._to;
  new_mess.type = mess._type;
  new_mess.body = mess.body || mess._body;
  new_mess.subject = mess.subject || mess._subject;
  new_mess.uid = mess._from.split('@')[0];
  new_mess.uname = mess._uname || (mess.userinfo && mess.userinfo.uname) || null;
  new_mess.currenttime = mess.currenttime || mess._currenttime;
  new_mess.avatarurl = mess._avatarurl || (mess.userinfo && mess.userinfo.avatarurl) || null;
  // 查询消息 没有 identity，level这个字段。。。
  new_mess.identity = mess._newidentity ? mess._newidentity : mess.userinfo ? mess.userinfo.newidentity : '1';
  // new_mess.level = isEmpty(mess._level) ? mess.userinfo.level : mess._level;
  new_mess.withdraw = mess.withdraw || 0;

  // 匹配 图片 不用转义，其余转义
  if (new_mess.id.match('activityimage')) {
    new_mess.body = new_mess.body.split('|')[1];
  } else {
    new_mess.body = Base64_decode(new_mess.body);
  }
  return new_mess;
};

/**
 * 节流 - 一段时间只会执行一次
 * @param {*} fn 执行函数
 * @param {*} delay 时间间隔
 * @returns
 */
export const throttle = (fn, delay = 1000) => {
  let timer = null;
  return function () {
    const context = this;
    const args = arguments;

    if (!timer) {
      fn.apply(context, args);
      timer = setTimeout(function () {
        timer = null;
      }, delay);
    }
  };
};

// 更优的 RAF 节流
export const rafThrottle = (fn) => {
  let rafId = null;
  return function (...args) {
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      fn.apply(this, args);
      rafId = null;
    });
  };
};

/**
 * 防抖 - 等待时间内如果再次触发，则重新计算等待时间
 * @param {*} fn 执行函数
 * @param { Number } delay 等待时间
 * @param { Boolean } immediate true（立即执行一次）
 * @returns
 */
export const debounce = (fn, wait = 1000, immediate = true) => {
  let timer = null;

  return function () {
    const context = this;
    const args = arguments;

    // 清空上一次操作
    timer && clearTimeout(timer);

    if (immediate) {
      // 立即执行一次
      const callNow = !timer;

      timer = setTimeout(function () {
        timer = null;
      }, wait);

      if (callNow) {
        fn.apply(context, args);
      }
    } else {
      // wait时间之后，任务执行
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    }
  };
};

// 双属性去重
export const uniqueDoubleName = function (arr, name1, name2) {
  const result = [];
  const obj = {};
  for (let i = 0; i < arr.length; i++) {
    if (!obj[arr[i][name1] + '|' + arr[i][name2]]) {
      result.push(arr[i]);
      obj[arr[i][name1] + '|' + arr[i][name2]] = true;
    }
  }

  return result;
};
export const sortColumnIndex = (index = '') => {
  return Number(index.substring(index.lastIndexOf('_') + 1));
};

// table 表格 拖拽宽，并记录保存宽
export const ColWidth = function (key, tableLabel) {
  const tableKey = key;
  let tableCol = [];
  const initColWidth = function (tableLabel) {
    const arr1 = JSON.parse(localStorage.getItem(tableKey));
    arr1 &&
      arr1.forEach((arr) => {
        const index = tableLabel.findIndex((item) => {
          return (item.prop && item.prop == arr.prop) || (item.param && item.param == arr.param);
        });
        index !== -1 && (tableLabel[index].width = arr.width);
      });
    tableCol = tableLabel;
  };

  initColWidth(tableLabel);

  this.setColWidth = function (newWidth, oldWidth, column) {
    if (newWidth < column.minWidth) {
      column.width = column.minWidth;
    } else {
      column.width = newWidth;
      tableCol.forEach((item) => {
        if (item.prop == column.property || item.param == column.property) {
          item.width = newWidth;
        }
      });
    }
    localStorage.setItem(tableKey, JSON.stringify(tableCol));
  };

  this.getColWidth = function (prop) {
    const index = tableCol.findIndex((col) => col.prop === prop || col.param === prop);
    return index >= 0 ? tableCol[index].width : '';
  };
};

// 比较两个
export const isObjectValueEqual = (newVal, oldVal) => {
  const newProps = Object.getOwnPropertyNames(newVal);
  const oldProps = Object.getOwnPropertyNames(oldVal);

  if (newProps.length !== oldProps.length) {
    return false;
  }
  for (let i = 0; i < newProps.length; i++) {
    const propName = newProps[i];

    const propNew = newVal[propName];
    const propOld = oldVal[propName];
    // 属性不存在
    if (!oldVal.hasOwnProperty(propName)) return false;
    // 同名属性类型不一样
    if (Object.prototype.toString.call(propNew) !== Object.prototype.toString.call(propOld)) return false;

    if (propNew instanceof Object) {
      if (!isObjectValueEqual(propNew, propOld)) {
        return false;
      }
    } else if (propNew !== propOld) {
      return false;
    }
  }
  return true;
};

// 将数字（整数）转为汉字
export function convertToChinaNum(num) {
  const arr1 = new Array('零', '一', '二', '三', '四', '五', '六', '七', '八', '九');
  const arr2 = new Array(
    '',
    '十',
    '百',
    '千',
    '万',
    '十',
    '百',
    '千',
    '亿',
    '十',
    '百',
    '千',
    '万',
    '十',
    '百',
    '千',
    '亿'
  ); // 可继续追加更高位转换值
  if (!num || isNaN(num)) {
    return '零';
  }
  const english = num.toString().split('');
  let result = '';
  for (let i = 0; i < english.length; i++) {
    const des_i = english.length - 1 - i; // 倒序排列设值
    result = arr2[i] + result;
    const arr1_index = english[des_i];
    result = arr1[arr1_index] + result;
  }
  // 将【零千、零百】换成【零】 【十零】换成【十】
  result = result.replace(/零(千|百|十)/g, '零').replace(/十零/g, '十');
  // 合并中间多个零为一个零
  result = result.replace(/零+/g, '零');
  // 将【零亿】换成【亿】【零万】换成【万】
  result = result.replace(/零亿/g, '亿').replace(/零万/g, '万');
  // 将【亿万】换成【亿】
  result = result.replace(/亿万/g, '亿');
  // 移除末尾的零
  result = result.replace(/零+$/, '');
  // 将【零一十】换成【零十】
  // result = result.replace(/零一十/g, '零十');//貌似正规读法是零一十
  // 将【一十】换成【十】
  result = result.replace(/^一十/g, '十');
  return result;
}

// 数字千分位格式化（仅对整数段加分隔符；有小数时保留小数部分原样，避免逗号插进小数位）
export const numberThousandthsFormat = (num) => {
  if (num === null || num === undefined || num === '' || Number.isNaN(Number(num))) return '0';
  const n = Number(num);
  const str = Math.abs(n % 1) > 1e-9 ? n.toFixed(2) : String(Math.trunc(n));
  const neg = str.startsWith('-');
  const raw = neg ? str.slice(1) : str;
  const [intPart, decPart] = raw.split('.');
  if (!intPart) return '0';
  // 自右向左每 3 位整数一组，在组前插入千分位逗号（\B 避免匹配开头）
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
  return neg ? `-${body}` : body;
};

/**
 *
 * @param {*} el 目标元素
 * @param {*} type true 退出全屏，false 开启全屏
 */
export function fullscreenClick(el, type) {
  if (type) {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitCancelFullScreen) {
      document.webkitCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  } else {
    if (el.requestFullscreen) {
      el.requestFullscreen();
    } else if (el.mozRequestFullScreen) {
      el.mozRequestFullScreen();
    } else if (el.webkitRequestFullScreen) {
      el.webkitRequestFullScreen();
    } else if (el.msRequestFullscreen) {
      el.msRequestFullscreen();
    }
  }
}

export function echartsYnumsFormart(num, splitNumber = 5) {
  if (num <= 5) return 5;
  if (num <= 10) return 10;

  let res = num;
  const arr = [];

  while (res) {
    arr.unshift(res % 10);
    res = Math.floor(res / 10);
  }
  if (arr[1] >= 5) {
    arr[0] += 1;
    arr[1] = 0;
  } else {
    arr[1] = 5;
  }
  const l = arr.length - 1;
  const result = arr[0] * Math.pow(10, l) + arr[1] * Math.pow(10, l - 1);
  return Math.ceil(result / 5 / splitNumber) * 5 * splitNumber;
}

export function echartsColor() {
  return [
    '#3D78FF',
    '#847DFF',
    '#FFD95E',
    '#49DB80',
    '#FF4800',
    '#00A7FF',
    '#6BD7B7',
    '#EA699B',
    '#FF8953',
    '#CD83FF',
    '#FA83E1',
    '#728EF1',
    '#FF7E78',
    '#E0E0E0'
  ];
}

// 路由拍平
export function flatRouteThreeLevel(routes) {
  function flatRoute(arr, head = '') {
    const result = [];
    arr.forEach((item) => {
      if (item.children) {
        const childRes = flatRoute(item.children, head ? `${head}/${item.path}` : item.path);
        result.push(...childRes);
      } else {
        result.push({
          ...item,
          path: item.path[0] === '/' ? item.path : head ? `${head}/${item.path}` : item.path
        });
      }
    });
    return result;
  }

  const res = flatRoute(routes);
  function redirectPaths(arr) {
    const result = [];
    arr.forEach((item) => {
      if (item.redirect) {
        const tem = {
          path: item.path,
          redirect: item.redirect
        };
        if (item.children) {
          const child = redirectPaths(item.children);
          if (child.length > 0) {
            tem.children = child;
          }
        }
        result.push(tem);
      }
    });
    return result;
  }

  const red = redirectPaths(routes);
  res.push(...red);

  return res;
}

/**
 * 粘贴复制过来的电话手机号码
 * @param {*} val
 * @returns
 */
export function pastePhoneNumber(val) {
  if (!val) return '';
  return val.match(/\d*/g).join('');
}

// 路由平铺
export function flatData(data, parentId = undefined) {
  return data.reduce((prev, curr) => {
    if (parentId != undefined) {
      Object.assign(curr, {
        parentId: parentId
      });
    }
    prev.push(curr);
    if (curr.childModuleList && curr.childModuleList.length > 0) {
      prev.push(...flatData(curr.childModuleList, curr.id));
    }
    return prev;
  }, []);
}

/**
 * AES对称解密
 */
export const decrypt = function (str, secretKey) {
  const key = CryptoJS.enc.Utf8.parse(secretKey);
  const decrypt = CryptoJS.AES.decrypt(str, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7
  });
  return CryptoJS.enc.Utf8.stringify(decrypt).toString();
};

/**
 * AES对称加密
 */
export const encrypt = function (str, secretKey) {
  const key = CryptoJS.enc.Utf8.parse(secretKey);
  const srcs = CryptoJS.enc.Utf8.parse(str);
  const encrypted = CryptoJS.AES.encrypt(srcs, key, { mode: CryptoJS.mode.ECB, padding: CryptoJS.pad.Pkcs7 });
  return encrypted.toString();
};

/**
 * RSA非对称加密
 */
export const rsAencrypt = function (str) {
  const encryptor = new JSEncrypt();
  encryptor.setPublicKey(RSA_KEY);
  return encryptor.encrypt(str);
};

/**
 * 在线预览文件 office、pdf
 */
export const previewFile = function (url) {
  if (url) {
    if (window.process?.versions?.electron) {
      return window
        ?.require('electron')
        ?.shell?.openExternal('http://file-view.test.lan.comein.cn/onlinePreview?url=' + encodeURIComponent(btoa(url)));
    }
    window.open('http://file-view.test.lan.comein.cn/onlinePreview?url=' + encodeURIComponent(btoa(url)));
  }
};

/**
 * 清除 内容中 \n | \\n
 * * **/
export const removeAllLineBreak = (str) => {
  const reg = new RegExp(/\\+n/g);
  return reg.test(str) ? str.replace(reg, '') : str;
};

/**
 * 替换文本中所有换行(\n)为<br/>
 *
 * @param  { String } str
 * @return { String }
 * **/
export const replaceAllLineBreakWithBr = (str) => {
  const reg = new RegExp(/\\+n/g);
  return reg.test(str) ? str.replace(reg, '<br/>') : str;
};

/**
 * 去除字符串中 <br/>
 *
 * @param { String } str
 * **/
export const removeBr = (str) => {
  const reg = new RegExp(/<br\s*\\?\/?>/gi);
  return reg.test(str) ? str.replace(reg, '') : str;
};

/**
 * 匹配html(以 p | div | a | span为例)
 * **/
export const resExpHtml = (str) => {
  const reg = new RegExp(/(<p|a|div|span).*(?=>)(.|\n)*?<\\?\/(p|a|div|span)>/g);
  return reg.test(str);
};

/** 验证是否token过期
 * @param {*} expires_in
 * @returns
 */
export const isAccessTokenExpired = (expires_in) => {
  if (!expires_in) return false;
  return expires_in < new Date().getTime();
};

/**
 * 关键字 高亮
 * @param keyword {String} 关键字
 * @param content {String} 完整句子
 * @param defaultClass {String} 类型
 * @returns <span class="highlightKeyword">${keyword}</span>...
 */
export const highlightKeywordsInContent = (keywords, content, defaultClass = 'highlightKeyword') => {
  if (!keywords || !content) return content;

  // 将空格分隔的关键词转换为数组
  const keywordArray = typeof keywords === 'string' ? keywords.split(' ') : keywords;

  // 长度大的关键词优先匹配
  keywordArray.sort((a, b) => b.length - a.length);

  // 生成正则表达式，同时匹配所有关键字 使用正则表达式一次性匹配所有关键字，而不是逐个匹配，以避免重复设置。
  const escapedKeywords = keywordArray.map((keyword) => keyword.replace(/[.*+?^${}()|[\]\\]/gi, '\\$&')).join('|');
  const keywordPattern = new RegExp(escapedKeywords, 'ig');

  // 缓存匹配结果
  const highlightedCache = {};
  content = content.replace(keywordPattern, (match) => {
    if (!highlightedCache[match]) {
      highlightedCache[match] = `<span class="${defaultClass}">${match}</span>`;
    }
    return highlightedCache[match];
  });

  return content;
};

/** 解析 WorkBuddy 内嵌场景可在当前 iframe 打开的可信业务地址。 */
const resolveWorkBuddyBusinessUrl = (target) => {
  if (!isWorkBuddyClient() || !target) return null;

  try {
    const targetUrl = new URL(target, window.location.origin);
    const businessOrigins = [window.location.origin, BRM_URL_HOST, ROADSHOW_URL_HOST]
      .filter(Boolean)
      .map((url) => new URL(url, window.location.origin).origin);
    return businessOrigins.includes(targetUrl.origin) ? targetUrl : null;
  } catch {
    // 无法解析的地址沿用原有打开方式
    return null;
  }
};

/**
 * a标签跳转
 * @param page = 跳转的page
 */
export const customLink = (page, useRouterPush = isElectron, title) => {
  const target = Object.prototype.toString.call(page) === '[object Object]' ? page.href : page;
  if (useRouterPush) {
    // 当开关为 true 时，使用 router.push 进行路由跳转
    // 假设你已经在当前作用域中导入了 Vue Router 的实例
    // electron 标签
    emitter.emit('routerPush', {
      href: target,
      title
    });

    // 在同一个页面内跳转时，触发路由刷新
    // 解析 target 获取 pathname（兼容字符串和对象两种传参方式）
    let targetPath = '';
    if (Object.prototype.toString.call(page) === '[object Object]') {
      targetPath = page.path;
    } else {
      // 从 href 字符串中解析 pathname（如 /cloudIndex?documentId=123 -> /cloudIndex）
      try {
        const url = new URL(target, location.origin);
        targetPath = url.pathname;
      } catch {
        // 如果解析失败，直接使用 target
        targetPath = target.split('?')[0];
      }
    }
    if (location.pathname === targetPath) {
      // 加上延时，防止上面的emitter.emit('routerPush'未完成跳转，导致router.refresh旧的路由
      setTimeout(() => {
        electron.dispatchEvent('router.refresh');
      }, 20);
    }
  } else {
    const workBuddyBusinessUrl = resolveWorkBuddyBusinessUrl(target);
    if (workBuddyBusinessUrl) {
      slsLogger.console({
        component: 'CustomLink',
        message: 'WorkBuddy内嵌页-当前iframe打开业务页面',
        extra: {
          origin: workBuddyBusinessUrl.origin,
          path: workBuddyBusinessUrl.pathname
        }
      });
      if (workBuddyBusinessUrl.origin === window.location.origin) {
        emitter.emit('routerPush', { href: `${workBuddyBusinessUrl.pathname}${workBuddyBusinessUrl.search}`, title });
        return;
      }
      window.location.assign(workBuddyBusinessUrl.href);
      return;
    }

    if (window.self === window.top) {
      // pdfPreview、commonRoadshowDetail页面服务于 electron 环境下
      if (['pdfPreview', 'commonRoadshowDetail'].includes(page.name) && page.query && page.query.url) {
        page.href = page.query.url;
      }

      const a = document.createElement('a');
      a.href = Object.prototype.toString.call(page) === '[object Object]' ? page.href : page;
      a.setAttribute('target', '_blank');
      document.body.appendChild(a);
      setTimeout(() => {
        a.click();
        a.remove();
      }, 100);
    } else {
      // 页面被嵌入 需要通过window.open打开新页面 新页面通过window.opener获取父窗口的localStorage
      if (window.process?.versions?.electron) {
        return window?.require('electron')?.shell?.openExternal(target);
      }
      window.open(target);
    }
  }
};

//睿远基金链接打开方式
export const ryjj_open_postMessage = (url) => {
  window.postMessage(
    {
      cmd: 'open',
      url: url
    },
    '*'
  );
  window.parent.postMessage(
    {
      cmd: 'open',
      url: url
    },
    '*'
  );
};

export const customStockDetailLink = (
  { type = 2, param, activeName = undefined, level = 1, trackKey = undefined, sourceType },
  router
) => {
  // if (!fullCode) return;
  const query = {};
  const url = window.location.search;
  if (url.includes('?')) {
    const urls = url.split('?');

    const getValue = (str) => {
      str.split('&').forEach((str) => {
        const index = str.indexOf('=');
        if (index > 0) {
          query[str.substring(0, index)] = str.substring(index + 1);
        }
      });
    };
    getValue(urls[1]);
  }
  const stockDetailQuery = {
    fullCode: type === 2 ? param : undefined,
    id: type === 1 || type === 3 ? param : undefined,
    superId: type === 1 && level === 0 ? param : undefined,
    sourceType: type === 3 ? sourceType : undefined,
    type,
    activeName,
    level: type === 1 ? level : undefined,
    trackKey,
    ...(isWorkBuddyClient() ? { openFrom: OPEN_FROM_WORKBUDDY } : {})
  };
  if (window.location.pathname === '/market/self-selected') {
    // 已在同一只个股/行业详情时不再重复跳转
    if (query && type === 2 && query.fullCode === param) return;
    if (query && type === 3 && query.id === param && query.sourceType === sourceType) return;
    if (query && type === 1 && (query.id === param || query.superId === param)) return;
  }
  // 自选页与 WorkBuddy 内嵌都走站内路由：WorkBuddy 会拦截 window.open 并把相对 href 整页灌进 iframe
  if (window.location.pathname === '/market/self-selected' || isWorkBuddyClient()) {
    router.push({ name: 'stockDetail', query: stockDetailQuery });
    return;
  }
  customLink(router.resolve({ name: 'stockDetail', query: stockDetailQuery }));
};

/**
 * 判断当前是否是合作页面
 */
export const isCooperationPage = () => {
  return (
    window.location.href.includes('/thirdparty/') ||
    location.search.includes('userChannel=') ||
    location.search.includes('comein_channel=')
  );
};

/**
 * 滚动到指定位置 适用于PC端 过度
 */
Math.easeInOutQuad = (t) => {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
};
export const smoothScroll = (element, to, duration = 500) => {
  duration = duration || 500; // 默认动画时长500ms
  const start = element.scrollTop;
  const change = to - start;
  const startDate = +new Date();

  // 定时器函数
  const scroll = function () {
    const currentTime = +new Date() - startDate;
    element.scrollTop = change * Math.easeInOutQuad(currentTime / duration) + start;

    if (currentTime < duration) {
      requestAnimationFrame(scroll);
    } else {
      element.scrollTop = to; // 确保最后的位置准确
    }
  };

  requestAnimationFrame(scroll);
};

/**
 * 根据文件地址获取blob数据
 * @param {*} fileUrl
 * @returns
 */
export const getBlob = (fileUrl) => {
  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', fileUrl, true);
      xhr.responseType = 'blob';
      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve(xhr.response);
        }
      };
      xhr.send();
      xhr.onerror = (error) => {
        if (isElectron) {
          electron.openExternal(fileUrl);
        } else {
          window.open(fileUrl);
        }
        reject(error);
      };
    } catch (error) {
      if (isElectron) {
        electron.openExternal(fileUrl);
      } else {
        window.open(fileUrl);
      }
      reject(error);
    }
  });
};

export const getBlobByFetch = async (fileUrl) => {
  try {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const blob = await response.blob();
    return blob;
  } catch (error) {
    throw new Error(error);
  }
};

// 用于将数组拆分成指定大小的块
export const arrayChunk = (array, size) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

/**
 * 回调函数失败时返回默认值
 * @param {*} cb 回调函数
 * @param {*} defaultValue 默认值
 * @returns {*} 返回值
 */
export const withDefaultOnError = (cb, defaultValue) => {
  try {
    return cb();
  } catch {
    return defaultValue;
  }
};

/**
 * 异步回调函数失败时返回默认值
 * @param {*} cb 回调函数
 * @param {*} defaultValue 默认值
 * @returns {*} 返回值
 */
export const withDefaultOnErrorAsync = async (cb, defaultValue) => {
  try {
    return await cb();
  } catch {
    return defaultValue;
  }
};

export const shieldPhone = (phone) => {
  if (!phone) return;
  const reg = /^(\d{3})\d{4}(\d{4})$/;
  return phone.replace(reg, '$1****$2');
};

/** 按当前语言格式化大数：中文 万/亿，英文 K/M/B */
export const fotmatBigPrice = (value) => {
  const num = Number(value);
  if (!num && num !== 0) return 0;
  return formatBigPriceByLocale(num) || '0';
};

/**
 * 将参数部分（search）转为对象
 *
 * @return { * }
 * **/
export const getUrlParam = (s) => {
  const urlParamObj = {};
  let search = s || location.search;

  if (search.indexOf('?') > -1) {
    search = search.substr(1);
    const searchArr = search.split('&');

    for (let i = 0, len = searchArr.length; i < len; i++) {
      urlParamObj[searchArr[i].split('=')[0]] = decodeURIComponent(searchArr[i].split('=')[1]);
    }
  }

  return urlParamObj;
};

/** 总数规格：中文 过千k/过万w，英文 K/M/B */
export const formatTotal = (total) => {
  if (total === 0) return total;
  return formatTotalByLocale(total);
};

export const formatAllTotal = (total, fixed = 1, thousandFormat = false) => {
  return formatAllTotalByLocale(total, fixed, thousandFormat);
};

// 兼容：对外仍从 api.js 导出 isEnLang
export const isEnLang = () => isEnLangFromLang();

// 金额千位分隔
export const formatThousandSeparator = (num = 0, fixed = 2, thousandFormat = true) => {
  const numStr = num.toFixed(fixed).toString();
  return thousandFormat ? numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : numStr;
};

// 文字截取省略号
export const getEllipsisStr = (str, len) => {
  if (typeof str === 'string' && str.length > len) {
    return str.slice(0, len) + '...';
  } else {
    return str;
  }
};

export const getHtml = (keyPoints, keyword, content, isId) => {
  let result = DOMPurify.sanitize(highlightKeywordsInContent(keyPoints.length ? keyPoints : keyword, content));
  if (isId) {
    // id要每三位隔开，也要高亮
    const indexs = [];
    const arr = Array.from(result);
    arr.forEach((item, index) => {
      if (item.match(/\d/)) {
        indexs.push(index);
      }
    });
    if (indexs.length === 9) {
      arr[indexs[2]] = arr[indexs[2]] + ' ';
      arr[indexs[5]] = arr[indexs[5]] + ' ';
    }
    result = arr.join('');
  }
  return result;
};

/**
 * 被分词匹配到的排在前面
 * @param {*} sourceArr 用于显示的数据数组
 * @param {*} keypointsArr 分词数组
 * @returns 数组
 */
export const sortLight = (sourceArr, keypointsArr, key = '') => {
  if (!isArr(sourceArr) || !isArr(keypointsArr)) return [];
  const lightArr = []; // 被分词匹配到的，需要高亮的
  const darkArr = [];
  sourceArr.forEach((item) => {
    const temp = keypointsArr.some((it) => {
      const keypoint = it.toLowerCase();
      if (isObject(item)) {
        if (key && item[key]?.toLowerCase()?.indexOf(keypoint) !== -1) {
          lightArr.push(item);
          return true;
        }
      } else {
        if (item?.toLowerCase()?.indexOf(keypoint) !== -1) {
          lightArr.push(item);
          return true;
        }
      }
    });
    if (!temp) {
      darkArr.push(item);
    }
  });
  return [...lightArr, ...darkArr];
};

/**
 * 根据匹配优先级排序
 * @param {*} sourceArr 源数据
 * @param {*} filterNameList 筛选命中列表
 * @param {*} keyPointsList 搜索命中列表
 * @param {*} key 数据源的key
 */
export const sortByMatch = (sourceArr, filterNameList, keyPointsList, key = '') => {
  // 将数据分为四类：
  // 1. 既被筛选命中又被搜索命中的（最高优先级）
  // 2. 仅被筛选命中的（次优先级）
  // 3. 仅被搜索命中的（第三优先级）
  // 4. 其他（最低优先级）
  const bothMatched = []; // 既筛选又搜索命中
  const onlyFilterMatched = []; // 仅筛选命中
  const onlySearchMatched = []; // 仅搜索命中
  const others = []; // 其他

  sourceArr.forEach((item) => {
    const itemContent = (key ? item[key] : item)?.toLowerCase();
    const isFilterMatched = filterNameList.some((it) => itemContent?.indexOf(it.toLowerCase()) !== -1);
    const isSearchMatched = keyPointsList.some((it) => itemContent?.indexOf(it.toLowerCase()) !== -1);

    if (isFilterMatched && isSearchMatched) {
      bothMatched.push(item);
    } else if (isFilterMatched) {
      onlyFilterMatched.push(item);
    } else if (isSearchMatched) {
      onlySearchMatched.push(item);
    } else {
      others.push(item);
    }
  });

  // 按优先级返回排序后的数组
  return [...bothMatched, ...onlyFilterMatched, ...onlySearchMatched, ...others];
};

/**
 * 根据筛选和搜索结果排序
 * @param {Array} sourceArr 源数据
 * @param {Object} options 选项
 * @param {Array} options.filterNames 筛选选中的名称列表
 * @param {Array} options.keyPoints 搜索关键词数组
 * @param {String} options.key 数据源的key
 * @returns 排序后的数组
 */
export const sortFilterAndSearch = (sourceArr = [], options = {}) => {
  if (!sourceArr || sourceArr.length === 0) return [];
  const { filterNames = [], keyPoints = [], key = '' } = options;
  // 搜索关键词
  let keyPointsList = [];
  if (typeof keyPoints === 'string' && keyPoints) {
    keyPointsList = [keyPoints];
  } else if (Array.isArray(keyPoints)) {
    keyPointsList = keyPoints?.filter((val) => val !== '');
  }
  // 仅筛选时，优先展示用户选择的（1对多时）
  if (filterNames.length > 0 && keyPointsList.length === 0) {
    return sortLight(sourceArr, filterNames, key);
  }
  // 仅搜索时，优先展示输入命中的（1对多时）
  if (filterNames.length === 0 && keyPointsList.length > 0) {
    return sortLight(sourceArr, keyPointsList, key);
  }
  // 同时筛选、搜索时
  // 结果列表：优先展示筛选命中行业
  // 用例：
  // 1）筛选命中A行业，搜索命中A行业 ---> 行业字段优先显示A行业，并标红搜索命中关键词
  // 2）筛选命中A行业，搜索命中B行业 ---> 行业字段优先显示A行业，点击“+N”展开时，标红搜索命中关键词
  // 3）筛选命中A,B行业，搜索命中B行业 ---> 行业字段优先显示B行业，并标红搜索命中关键词
  if (filterNames.length > 0 && keyPointsList.length > 0) {
    return sortByMatch(sourceArr, filterNames, keyPointsList, key);
  }
  return sourceArr;
};

/**
 * 根据搜索关键词 获取匹配的股票关键词列表（用于股票标签）
 * @param {Array} stockList 接口返回的股票列表
 * @param {String} keyword 搜索关键词
 * @param {Array} keyPoints 搜索关键词列表
 * @returns {Array|String} 返回匹配的股票关键词列表/关键词
 */
export const getMatchStockKeyPoints = (stockList, keyword, keyPoints) => {
  const keyPointsByStock = [];
  const matchStock = stockList.find((v) => v.fullCode?.toUpperCase() === keyword?.toUpperCase());
  if (matchStock) {
    if (matchStock.code && matchStock.market) {
      keyPointsByStock.push(`${matchStock.code}.${matchStock.market.toUpperCase()}`);
    }
    if (matchStock.fullCode) {
      keyPointsByStock.push(matchStock.fullCode);
    }
  }
  let res = Array.isArray(keyPoints)
    ? [...keyPoints, ...keyPointsByStock]
    : typeof keyPoints === 'string'
    ? keyPoints.split().concat(keyPointsByStock)
    : keyPointsByStock;
  res = [...new Set(res)].filter((v) => v);
  return res.length > 0 ? res : keyword;
};

/**
 * 获取市场类型分区股市类型
 * @param {*} marketType 股市类型
 * @returns 类型 1 A股 2 港股 3 美股 4 其他
 */
export const getFullcodeMarketType = (marketType) => {
  switch (String(marketType).toUpperCase()) {
    case 'SZ':
    case 'SH':
    case 'BJ':
      // A股
      return 1;
    case 'HK':
      // 港股
      return 2;
    case 'US':
      // 美股
      return 3;
    default:
      // 其他
      return 4;
  }
};

// 数组去重(简易版)
export const uniqueArr = (array) => [...new Set(array)];

// 判断参数是否被encodeURIComponent 编码过
export const isEncoded = (str) => (str ? /%[0-9A-Fa-f]{2}/.test(str) : false);

// 不再提醒
export const accountStorage = {
  // 键为常量，值为{账号id: [企业id]}
  get: (key) => {
    const jmAuthInfo = storage.get(LOCAL_STORAGE_KEY.JM_AUTH_INFO, true) || {};
    const account = jmAuthInfo.uid;
    const organizationId = jmAuthInfo.organizationId;
    const noticeNote = storage.get(key) || {};
    if (noticeNote[account]) {
      if (noticeNote[account].indexOf(organizationId) !== -1) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  },
  set: (key, value, fix) => {
    // 有了fix为前提，并且value为true就不会再提示了，即使重新登录也不会
    const jmAuthInfo = storage.get(LOCAL_STORAGE_KEY.JM_AUTH_INFO, true) || {};
    const account = jmAuthInfo.uid;
    const organizationId = jmAuthInfo.organizationId;
    const noticeNote = storage.get(key) || {};
    if (!noticeNote[account]) {
      noticeNote[account] = [];
    }
    if (value) {
      if (noticeNote[account].indexOf(organizationId) === -1) {
        noticeNote[account].push(organizationId);
      }
    } else {
      if (noticeNote[account].indexOf(organizationId) !== -1) {
        const index = noticeNote[account].indexOf(organizationId);
        noticeNote[account].splice(index, 1);
      }
    }
    storage.set(key, noticeNote);
    if (fix) {
      // localstorage退出登录时会被clear，而fix则标记哪些不需要clear
      const DO_NOT_MOVE = storage.get(LOCAL_STORAGE_KEY.DO_NOT_MOVE_ANYWAY) || {};
      if (value) {
        if (Object.keys(DO_NOT_MOVE).indexOf(key) === -1) {
          DO_NOT_MOVE[key] = {
            [account]: [organizationId]
          };
        } else {
          if (Object.keys(DO_NOT_MOVE[key]).indexOf(account) === -1) {
            DO_NOT_MOVE[key][account] = [organizationId];
          } else {
            DO_NOT_MOVE[key][account].push(organizationId);
          }
        }
      } else {
        if (Object.keys(DO_NOT_MOVE).indexOf(key) !== -1) {
          if (Object.keys(DO_NOT_MOVE[key]).indexOf(account) !== -1) {
            const orgIndex = DO_NOT_MOVE[key][account].indexOf(organizationId);
            DO_NOT_MOVE[key][account].splice(orgIndex, 1);
          }
        }
      }
      storage.set(LOCAL_STORAGE_KEY.DO_NOT_MOVE_ANYWAY, DO_NOT_MOVE);
    }
  }
};

// 格式化大小 字节
export const formatBytes = (bytes) => {
  const formatNumber = (num) => (num % 1 === 0 ? num.toFixed(0) : num.toFixed(2));
  if (bytes < 1024) return { value: formatNumber(bytes), unit: 'KB', complete: `${formatNumber(bytes)} KB` };
  const mBytes = bytes / 1024;
  if (mBytes < 1024) return { value: formatNumber(mBytes), unit: 'MB', complete: `${formatNumber(mBytes)} MB` };
  const gBytes = mBytes / 1024;
  return { value: formatNumber(gBytes), unit: 'GB', complete: `${formatNumber(gBytes)} GB` };
};
// 格式化大小 字节
export const formatBytesToMb = (bytes) => {
  const formatNumber = (num) => (num % 1 === 0 ? num.toFixed(0) : num.toFixed(2));
  const mBytes = bytes / 1024;
  return { value: formatNumber(mBytes), unit: 'MB', complete: `${formatNumber(mBytes)} MB` };
};

// 获取文件名后缀
export const getSuffix = (str) => {
  const arr = str.split('.');
  return arr[arr.length - 1];
};

// 把'文件名部分.文件名部分.mp4'或者'文件名部分.mp4'的后缀去除掉
export const getFileName = (str) => {
  const arr = str.split('.');
  return arr.splice(0, arr.length - 1).join('.');
};

// 去掉字符串的html标签
export const removeHTMLTags = (str) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, 'text/html');
  return doc.body.textContent || '';
};

// 摘要纯文本化时需要补分隔空格的元素：块级容器与 <br>，避免相邻段落/列表项文字首尾粘连
const SUMMARY_SEPARATOR_SELECTOR = 'p, div, br, li, ul, ol, h1, h2, h3, h4, h5, h6, blockquote, pre';

/**
 * 将富文本 / Markdown 渲染后的 HTML 摘要转为折叠空白的单行纯文本。
 *
 * 摘要区依赖 `-webkit-line-clamp` 截断，而该属性只能配合 `display: -webkit-box` 生效；
 * Safari 17.1 以下（如 16.6，macOS Monterey 能装到的最后一版）在 `-webkit-box` 内含块级
 * 元素时会算错行盒位置，表现为文字相互重叠，故块级结构须在进入截断容器前消除。
 *
 * @param {string} html 待转换的 HTML 字符串
 * @returns {string} 单行纯文本；入参为空或无文本内容时返回空字符串
 */
export const htmlToSummaryText = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  // 表格原本在摘要里被样式隐藏，纯文本化后同样丢弃，避免整表单元格文字挤占有限的展示行数
  doc.body.querySelectorAll('table').forEach((node) => node.remove());
  // textContent 会把相邻块级元素的首尾直接拼接，先在其后插入空格以保留词间分隔
  doc.body.querySelectorAll(SUMMARY_SEPARATOR_SELECTOR).forEach((node) => node.after(' '));
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

/**
 * 添加水印参数
 * @params  { url: String }url 图片地址
 * @params  { type, mark } type image: 文档水印 pdf：阿里云水印 mark 水印内容
 * @return { url } url 图片地址
 * **/
export const addWatermark = (url, { type = 'image', mark = '' } = {}) => {
  const userInfo = storage.get(LOCAL_STORAGE_KEY.JM_AUTH_INFO, true)?.userinfo || {};
  if (!userInfo) return url;

  const userLogin_base64 = Base64_encode(mark || userInfo.login);

  if (type == 'image') {
    const watermark = `x-oss-process=image/watermark,type_d3F5LXplbmhlaQ,size_20,text_${userLogin_base64},color_D7D7D7,t_50,g_center,x_0,y_0,rotate_337,fill_1,padx_300,pady_300`;
    url += (url.indexOf('?') > -1 ? '&' : '?') + watermark;
  } else {
    const watermark =
      'text_' +
      encodeURIComponent(mark || userInfo.login) +
      ',color_D7D7D7,size_12rem,opacity_0.3,rotate_315,col_4,row_3';
    url += (url.indexOf('?') > -1 ? '&' : '?') + 'watermark=' + watermark;

    const query = url.split('?')[1];
    url = url.split('?')[0] + '?options=' + encodeURIComponent(encrypt(query));
  }
  return url;
};

export const isExistWatermark = (url, { type = 'image' } = {}) => {
  if (type == 'image') {
    return url.indexOf('watermark') > -1 && url.indexOf('watermark,type_d3F5LXplbmhlaQ,,size_20,text_') > -1;
  } else {
    return (
      url.indexOf('watermark') > -1 && url.indexOf('color_D7D7D7,size_12rem,opacity_0.3,rotate_315,col_4,row_3') > -1
    );
  }
};

// 根据文件大写后缀名展示对应图标
export const getFileIcon = (typeName) => {
  const upperTypeName = typeName?.toUpperCase();

  const typeMap = {
    PDF: 'pdf.png',
    DOCX: 'doc.png',
    DOC: 'doc.png',
    XLSX: 'xlsx.png',
    XLSM: 'xlsx.png',
    PPT: 'ppt.png',
    TXT: 'txt.png',
    JPG: 'img.png',
    JPEG: 'img.png',
    PNG: 'img.png',
    XLS: 'xlsx.png',
    PPTX: 'ppt.png',
    CSV: 'csv.png',
    MD: 'markdown.png',
    HTML: 'html.png'
  };
  const imgPath = typeMap[upperTypeName] || 'file.png';
  if (inVite()) {
    return new URL(`/src/assets/img/ai-chat/${imgPath}`, import.meta.url).href;
  }
  return require(`../assets/img/ai-chat/${imgPath}`);
};

/**
 *  webpack转vite注意点：vite获取本地图片,此方法主要用于替代原require引入图片的方式，比如props的默认图片，变量中存储的图片，直接写在img src中的图片不受影响
 *  原理/构建工具的处理方式‌：
 *  ‌直接写在src中‌：当图片路径直接写在src属性中时，构建工具会在编译阶段识别并处理这些路径。例如，webpack会将小图片转换为base64格式，大图片则转换为相对路径（如/dist/img/logo.png）。这样，最终生成的资源文件会按照构建工具的配置进行处理，确保图片能够正确加载‌
 *  ‌通过变量传入‌：如果通过变量传入图片路径，构建工具在编译阶段不会处理这些变量中的路径。因此，最终打包后的文件中可能找不到对应的图片路径，导致图片加载失败‌
 * @param {*} fileName 图片名称
 * @param {*} path 路径，默认assets/img
 * @returns 图片
 */
export const getImageUrl = (path) => {
  if (inVite()) {
    return new URL(/* webpackIgnore: true */ `/src/assets/${path}`, import.meta.url).href;
  }
  if (path.startsWith('img/')) {
    return require(`../assets/img/${path.slice(4)}`);
  }
  if (path.startsWith('svg/')) {
    return require(`../assets/svg/${path.slice(4)}`);
  }
};
/**
 * webpack转vite注意点：区分require.context和import.meta.glob数据处理
 * @param {*} context 文件资源
 * @param {*} needDefault 是否取default(webpack路由)
 * @param {*} excludeKeyList 略过的文件列表
 * @returns 图片
 */
export function handleImportAllFileData(context, needDefault, excludeKeyList) {
  const modules = {};
  const getFileName = (path) => {
    const lastSlashIndex = path.lastIndexOf('/'); // 移除开头的./
    const lastPointIndex = path.lastIndexOf('.'); // 移除.js扩展名
    const fileName = path.substring(lastSlashIndex + 1, lastPointIndex);
    return fileName;
  };
  if (inVite()) {
    // Vite 环境：处理import.meta.glob数据
    Object.entries(context).forEach(([path, initModule]) => {
      if (excludeKeyList && excludeKeyList.find((item) => path.indexOf(item) > -1)) return;
      const name = getFileName(path);
      modules[name] = initModule.default;
    });
  } else {
    // Webpack 环境：处理 require.context 数据
    context.keys().forEach((key) => {
      if (excludeKeyList && excludeKeyList.find((item) => key.indexOf(item) > -1)) return;
      modules[getFileName(key)] = needDefault ? context(key).default : context(key);
    });
  }

  return modules;
}

//是否手机端或者浏览器手机模式
export const isH5 =
  /(iPhone|iPad|iPod|Android)/i.test(navigator.userAgent.toLowerCase()) ||
  /(Mobile)/i.test(navigator.userAgent.toLowerCase());

/**
 * 将HTML内容复制到剪贴板，支持粘贴到Word时保留样式（内部方法）
 * @param {string} htmlContent - HTML内容（不需要包含完整的html文档结构）
 * @param {string} [customStyle] - 可选的自定义CSS样式
 * @returns {Promise}
 */
const copyHtmlContentToClipboard = async (htmlContent) => {
  if (!htmlContent) {
    throw new Error('内容不能为空');
  }

  // 创建完整的HTML文档结构（Word需要这个格式）
  // const defaultStyle = `
  //   body {
  //     font-family: "Microsoft YaHei", "SimSun", Arial, sans-serif;
  //     font-size: 12pt;
  //     line-height: 1.5;
  //     margin: 0;
  //     padding: 8px;
  //   }
  //   p { margin: 6pt 0; }
  //   h1, h2, h3, h4, h5, h6 { font-weight: bold; margin: 12pt 0 6pt 0; }
  //   ul, ol { margin: 6pt 0; padding-left: 20pt; }
  //   table { border-collapse: collapse; width: 100%; margin: 0; }
  //   th, td { border: 1px solid #e5e6eb; padding: 8px 12px; text-align: left; }
  //   th { background-color: #f4f7fe; font-weight: 500; }
  //   code { font-family: "Courier New", monospace; background-color: #f5f5f5; }
  //   pre { font-family: "Courier New", monospace; background-color: #f5f5f5; padding: 8pt; border: 1px solid #ddd; }
  // `;

  // <style>${customStyle || defaultStyle}</style>;
  //
  const htmlWithHeader = `
    <html>
      <head>
        <meta charset="utf-8">
      </head>
      <body>
        ${htmlContent}
      </body>
    </html>
  `;

  try {
    // 使用现代Clipboard API
    if (navigator.clipboard && window.ClipboardItem) {
      // 创建临时元素来提取纯文本内容
      const tempDivForText = document.createElement('div');
      tempDivForText.innerHTML = htmlContent;
      const plainText = tempDivForText.textContent || tempDivForText.innerText || '';

      const htmlBlob = new Blob([htmlWithHeader], { type: 'text/html' });
      const textBlob = new Blob([plainText], { type: 'text/plain' });

      const clipboardItem = new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': textBlob
      });

      await navigator.clipboard.write([clipboardItem]);
      return Promise.resolve();
    } else {
      // 降级方案：使用execCommand
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'fixed';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      tempDiv.contentEditable = true;
      tempDiv.innerHTML = htmlContent;
      document.body.appendChild(tempDiv);

      // 选中内容
      const range = document.createRange();
      range.selectNodeContents(tempDiv);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      // 复制
      const success = document.execCommand('copy');
      selection.removeAllRanges();
      document.body.removeChild(tempDiv);

      if (success) {
        return Promise.resolve();
      } else {
        throw new Error('复制失败，请手动复制');
      }
    }
  } catch (error) {
    console.error('复制到Word失败:', error);
    throw error;
  }
};
/**
 * 将富文本HTML内容复制到剪贴板，支持粘贴到Word时保留样式
 * @param {string} htmlContent - HTML格式的富文本内容
 * @returns {Promise}
 */
export const copyHtmlToWord = async (htmlContent) => {
  if (!htmlContent) {
    throw new Error('内容不能为空');
  }

  // 清理和优化HTML内容，使其更适合Word
  const cleanHtml = (html) => {
    // 创建一个临时div来处理HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // 移除一些Word不支持的属性
    const removeAttributes = (element) => {
      // 移除事件处理器属性
      ['onmouseenter', 'onclick', 'onmouseleave', 'data-index'].forEach((attr) => {
        element.removeAttribute(attr);
      });

      // 递归处理子元素
      Array.from(element.children).forEach((child) => {
        removeAttributes(child);
      });
    };

    removeAttributes(tempDiv);

    // 将一些常见的markdown样式转换为Word友好的格式
    // 确保段落有正确的格式
    const paragraphs = tempDiv.querySelectorAll('p');
    paragraphs.forEach((p) => {
      if (!p.style.marginTop && !p.style.marginBottom) {
        p.style.marginTop = '0';
        p.style.marginBottom = '0';
      }
    });

    // 处理标题样式
    const headings = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6');
    headings.forEach((heading) => {
      const level = parseInt(heading.tagName.charAt(1));
      heading.style.fontSize = `${24 - level * 2}pt`;
      heading.style.fontWeight = 'bold';
      heading.style.marginTop = '12pt';
      heading.style.marginBottom = '6pt';
    });

    // 处理列表样式
    const lists = tempDiv.querySelectorAll('ul, ol');
    lists.forEach((list) => {
      list.style.marginLeft = '20pt';
      list.style.marginTop = '6pt';
      list.style.marginBottom = '6pt';
    });

    // 处理代码块
    const codeBlocks = tempDiv.querySelectorAll('pre, code');
    codeBlocks.forEach((code) => {
      code.style.fontFamily = 'Courier New, monospace';
      code.style.backgroundColor = '#f5f5f5';
      if (code.tagName === 'PRE') {
        code.style.padding = '8pt';
        code.style.border = '1px solid #ddd';
      }
    });

    // 处理表格
    const tables = tempDiv.querySelectorAll('table');
    tables.forEach((table) => {
      table.style.borderCollapse = 'collapse';
      table.style.width = '100%';
      const cells = table.querySelectorAll('th, td');
      cells.forEach((cell) => {
        cell.style.border = '1px solid #000';
        cell.style.padding = '4pt';
      });
    });

    return tempDiv.innerHTML;
  };

  const cleanedHtml = cleanHtml(htmlContent);

  // 调用公共复制方法
  return await copyHtmlContentToClipboard(cleanedHtml);
};

/**
 * 将表格数据转换为HTML表格并复制到剪贴板，支持粘贴到Word时保留表格样式
 * @param {Object} header - 表头对象，例如 {name: '指标名称', value: '数据', date: '日期'}
 * @param {Array} content - 内容数组，每个元素包含 {name: '分组名', list: [{name: '指标名', value: '数据', date: '日期'}]}
 * @returns {Promise}
 */
export const copyTableToWord = async (header, content) => {
  if (!header || !content || !Array.isArray(content)) {
    throw new Error('参数格式错误：header应为对象，content应为数组');
  }

  // 构建HTML表格
  const buildTableHtml = () => {
    // 获取表头列名（支持动态列）
    const headerKeys = Object.keys(header);
    const headerValues = headerKeys.map((key) => header[key]);

    let tableHtml =
      '<table style="border-collapse: collapse; width: 100%; font-family: "Microsoft YaHei", "SimSun", Arial, sans-serif; font-size: 12pt;">';

    // 表头行
    tableHtml += '<thead><tr style="background-color: #f4f7fe;">';
    headerValues.forEach((headerText, index) => {
      const isFirst = index === 0;
      const isLast = index === headerValues.length - 1;
      tableHtml += `<th style="border: 1px solid #e5e6eb; padding: 8px 12px; text-align: left; font-weight: 500; ${
        isFirst ? 'width: 40%;' : isLast ? 'width: 20%;' : 'width: 40%;'
      }">${headerText || ''}</th>`;
    });
    tableHtml += '</tr></thead>';

    // 表格内容
    tableHtml += '<tbody>';

    content.forEach((group, groupIndex) => {
      const groupName = group.name || '';
      const groupList = Array.isArray(group.list) ? group.list : [group.list].filter(Boolean);

      // 如果有分组名且有数据，添加分组行
      if (groupName && groupList.length > 0) {
        tableHtml += '<tr style="background-color: #f9fafb;">';
        tableHtml += `<td colspan="${headerKeys.length}" style="border: 1px solid #e5e6eb; padding: 8px 12px; font-weight: 500;">${groupName}</td>`;
        tableHtml += '</tr>';
      }

      // 添加数据行
      groupList.forEach((item, itemIndex) => {
        const isLastInGroup = itemIndex === groupList.length - 1;
        const isLastGroup = groupIndex === content.length - 1;
        const isLastRow = isLastInGroup && isLastGroup;

        tableHtml += '<tr>';
        headerKeys.forEach((key) => {
          const cellValue = item[key] || '';

          // 设置单元格样式
          let cellStyle = 'border: 1px solid #e5e6eb; padding: 8px 12px;';
          if (isLastRow) {
            cellStyle += ' border-bottom: 1px solid #e5e6eb;';
          }

          tableHtml += `<td style="${cellStyle}">${cellValue}</td>`;
        });
        tableHtml += '</tr>';
      });
    });

    tableHtml += '</tbody></table>';
    return tableHtml;
  };

  const tableHtml = buildTableHtml();

  // 调用公共复制方法
  return await copyHtmlContentToClipboard(tableHtml);
};

/**
 * 生成合法的 Excel 工作表名称。
 * Excel 工作表名称存在两类限制，违反任意一条 xlsx 都会抛错：
 * 1. 不能包含特殊字符 : \ / ? * [ ]（图表标题常含「/」分隔，如「精细化工 / 化纤 / PTA聚酯」）
 * 2. 长度不能超过 31 个字符
 * @param {string} [rawName] - 原始名称（通常为图表/表格标题）
 * @returns {string} 过滤非法字符并截断后的合法工作表名称
 * @example
 * getValidSheetName('精细化工 / 化纤 / PTA聚酯'); // => '精细化工 _ 化纤 _ PTA聚酯'
 */
const getValidSheetName = (rawName) => {
  // 兜底为默认名，避免空字符串导致 xlsx 报错
  const name = rawName || 'Sheet1';
  // 将 Excel 不允许的字符统一替换为下划线，保留标题语义
  const sanitized = name.replace(/[:\\/?*[\]]/g, '_');
  // 超长则截断并以省略号结尾，确保不超过 31 字符上限
  if (sanitized.length > 31) {
    return sanitized.substring(0, 28) + '...';
  }
  return sanitized;
};

/**
 * 导出Excel文件（带样式）
 * @param {Object} options - 导出配置选项
 * @param {string} options.title - 表格标题
 * @param {Array<string>} options.columns - 列名数组
 * @param {Array<Object>} options.data - 数据数组，每个对象对应一行数据
 * @param {string} options.fileName - 文件名（不含扩展名）
 * @param {string} options.sheetName - Sheet名称，默认为title
 * @returns {Promise<void>}
 */
export const exportToExcel = async ({ title, columns, data, fileName, sheetName, defaultColWidth = 25 }) => {
  try {
    // 动态导入 xlsx-js-style 库（支持样式）
    const XLSX = await import('xlsx-js-style');

    // 创建工作簿
    const wb = XLSX.utils.book_new();

    // 处理工作表名称：过滤非法字符并截断长度
    const finalSheetName = getValidSheetName(sheetName || title);

    // 准备数据：标题 + 表头 + 内容
    const wsData = [];

    // 第一行：标题
    wsData.push([title]);

    // 第二行：表头
    wsData.push(columns);

    // 内容行
    data.forEach((row) => {
      const rowData = columns.map((col) => row[col] ?? '-');
      wsData.push(rowData);
    });

    // 创建工作表
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // 设置列宽（默认宽度）
    const colWidths = columns.map(() => ({ wch: defaultColWidth }));
    ws['!cols'] = colWidths;

    // 合并标题单元格（第一行合并所有列）
    const mergeRange = {
      s: { r: 0, c: 0 },
      e: { r: 0, c: columns.length - 1 }
    };
    ws['!merges'] = [mergeRange];

    // 边框样式（网格线）
    const borderStyle = {
      top: { style: 'thin', color: { rgb: 'D1D5DB' } },
      bottom: { style: 'thin', color: { rgb: 'D1D5DB' } },
      left: { style: 'thin', color: { rgb: 'D1D5DB' } },
      right: { style: 'thin', color: { rgb: 'D1D5DB' } }
    };

    // 通用样式配置
    const titleStyle = {
      font: { name: '仿宋_GB2312', sz: 12, bold: true },
      alignment: { horizontal: 'left', vertical: 'center' },
      fill: { fgColor: { rgb: 'F1F2F3' } },
      border: borderStyle
    };

    const headerStyle = {
      font: { name: '仿宋_GB2312', sz: 12, bold: true },
      alignment: { horizontal: 'center', vertical: 'center', width: 400 },
      fill: { fgColor: { rgb: 'F8F9FA' } },
      border: borderStyle
    };

    const contentStyle = {
      font: { name: '仿宋_GB2312', sz: 12, bold: false },
      alignment: { horizontal: 'left', vertical: 'center', width: 400 },
      border: borderStyle
    };

    const contentStyleRight = {
      font: { name: '仿宋_GB2312', sz: 12, bold: false },
      alignment: { horizontal: 'right', vertical: 'center', width: 400 },
      border: borderStyle
    };

    const zebraStyle = {
      fill: { fgColor: { rgb: 'F8F9FA' } },
      border: borderStyle
    };

    // 设置标题行样式
    const titleCell = ws['A1'];
    if (titleCell) {
      titleCell.s = titleStyle;
    }

    // 换行配置
    const alignment = {
      wrapText: true,
      vertical: 'top',
      horizontal: 'left'
    };

    // 设置表头样式（第二行）
    columns.forEach((col, idx) => {
      const cellRef = XLSX.utils.encode_cell({ r: 1, c: idx });
      const cell = ws[cellRef];
      if (cell) {
        cell.s = headerStyle;
      }
    });

    // 设置内容样式（从第三行开始，斑马纹）
    data.forEach((row, rowIdx) => {
      const actualRowIdx = rowIdx + 2; // 跳过标题和表头
      const isOddRow = rowIdx % 2 === 1; // 奇数行显示底纹

      columns.forEach((col, colIdx) => {
        const cellRef = XLSX.utils.encode_cell({ r: actualRowIdx, c: colIdx });
        const cell = ws[cellRef];
        if (cell) {
          const value = row[col];
          const isNumeric = typeof value === 'number' || (!isNaN(parseFloat(value)) && value !== '-');
          const baseStyle = colIdx === 0 ? contentStyle : isNumeric ? contentStyleRight : contentStyle;

          cell.s = isOddRow ? { ...baseStyle, ...zebraStyle, alignment } : { ...baseStyle, alignment };
        }
      });
    });

    // 添加工作表到工作簿
    XLSX.utils.book_append_sheet(wb, ws, finalSheetName);

    // 导出文件
    const fullFileName = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
    XLSX.writeFile(wb, fullFileName);
  } catch (error) {
    console.error('导出Excel失败:', error);
    throw error;
  }
};

/**
 * 将Markdown表格转换为Excel Blob对象（不直接下载）
 * @param {string} markdownContent Markdown内容
 * @param {string} title 表格标题
 * @param {string} sheetName 工作表名称
 * @param {number} defaultColWidth 默认列宽
 * @returns {Promise<Blob>} Excel文件的Blob对象
 */
export const markdownTableToExcelBlob = async ({
  markdownContent,
  title = '表格',
  sheetName,
  defaultColWidth = 25
}) => {
  try {
    // 动态导入 xlsx-js-style 库（支持样式）
    const XLSX = await import('xlsx-js-style');
    // 动态导入 replaceWithLookbehind 函数
    const { replaceWithLookbehind } = await import('@/utils/regexHelper');

    // 先过滤掉标签索引 [数字] 格式（不在 = 或 : 后面的）
    const filteredMarkdown = replaceWithLookbehind(String(markdownContent), '(?<![=:]\\s)\\[(\\d+)\\]', '');

    // 将Markdown转换为HTML
    const transformToHtml = await getTransformToHtmlAsync();
    const htmlContent = transformToHtml.render(filteredMarkdown);

    // 解析表格内容
    const data = parseTableContent(htmlContent);
    const tableHtml = data.find((item) => item.type === 'table');

    if (!tableHtml) {
      throw new Error('未找到表格数据');
    }

    // 提取表格数据
    const tableData = extractTableData(tableHtml.content);

    if (!tableData || !tableData.data || tableData.data.length === 0) {
      throw new Error('表格数据为空');
    }

    // 创建工作簿
    const wb = XLSX.utils.book_new();

    // 处理工作表名称：过滤非法字符并截断长度
    const finalSheetName = getValidSheetName(sheetName || title);

    // 准备数据：标题 + 表头 + 内容
    const wsData = [];

    // 第一行：标题
    wsData.push([title]);

    // 第二行：表头
    wsData.push(tableData.columns);

    // 内容行
    tableData.data.forEach((row) => {
      const rowData = tableData.columns.map((col) => row[col] ?? '-');
      wsData.push(rowData);
    });

    // 创建工作表
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // 设置列宽（默认宽度）
    const colWidths = tableData.columns.map(() => ({ wch: defaultColWidth }));
    ws['!cols'] = colWidths;

    // 合并标题单元格（第一行合并所有列）
    const mergeRange = {
      s: { r: 0, c: 0 },
      e: { r: 0, c: tableData.columns.length - 1 }
    };
    ws['!merges'] = [mergeRange];

    // 边框样式（网格线）
    const borderStyle = {
      top: { style: 'thin', color: { rgb: 'D1D5DB' } },
      bottom: { style: 'thin', color: { rgb: 'D1D5DB' } },
      left: { style: 'thin', color: { rgb: 'D1D5DB' } },
      right: { style: 'thin', color: { rgb: 'D1D5DB' } }
    };

    // 通用样式配置
    const titleStyle = {
      font: { name: '仿宋_GB2312', sz: 12, bold: true },
      alignment: { horizontal: 'left', vertical: 'center' },
      fill: { fgColor: { rgb: 'F1F2F3' } },
      border: borderStyle
    };

    const headerStyle = {
      font: { name: '仿宋_GB2312', sz: 12, bold: true },
      alignment: { horizontal: 'center', vertical: 'center', width: 400 },
      fill: { fgColor: { rgb: 'F8F9FA' } },
      border: borderStyle
    };

    const contentStyle = {
      font: { name: '仿宋_GB2312', sz: 12, bold: false },
      alignment: { horizontal: 'left', vertical: 'center', width: 400 },
      border: borderStyle
    };

    const contentStyleRight = {
      font: { name: '仿宋_GB2312', sz: 12, bold: false },
      alignment: { horizontal: 'right', vertical: 'center', width: 400 },
      border: borderStyle
    };

    const zebraStyle = {
      fill: { fgColor: { rgb: 'F8F9FA' } },
      border: borderStyle
    };

    // 设置标题行样式
    const titleCell = ws['A1'];
    if (titleCell) {
      titleCell.s = titleStyle;
    }

    // 换行配置
    const alignment = {
      wrapText: true,
      vertical: 'top',
      horizontal: 'left'
    };

    // 设置表头样式（第二行）
    tableData.columns.forEach((col, idx) => {
      const cellRef = XLSX.utils.encode_cell({ r: 1, c: idx });
      const cell = ws[cellRef];
      if (cell) {
        cell.s = headerStyle;
      }
    });

    // 设置内容样式（从第三行开始，斑马纹）
    tableData.data.forEach((row, rowIdx) => {
      const actualRowIdx = rowIdx + 2; // 跳过标题和表头
      const isOddRow = rowIdx % 2 === 1; // 奇数行显示底纹

      tableData.columns.forEach((col, colIdx) => {
        const cellRef = XLSX.utils.encode_cell({ r: actualRowIdx, c: colIdx });
        const cell = ws[cellRef];
        if (cell) {
          const value = row[col];
          const isNumeric = typeof value === 'number' || (!isNaN(parseFloat(value)) && value !== '-');
          const baseStyle = colIdx === 0 ? contentStyle : isNumeric ? contentStyleRight : contentStyle;

          cell.s = isOddRow ? { ...baseStyle, ...zebraStyle, alignment } : { ...baseStyle, alignment };
        }
      });
    });

    // 添加工作表到工作簿
    XLSX.utils.book_append_sheet(wb, ws, finalSheetName);

    // 将工作簿转换为Blob
    const excelBuffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    return blob;
  } catch (error) {
    console.error('将Markdown表格转换为Excel失败:', error);
    throw error;
  }
};

/**
 * 从HTML表格中提取数据,
 * @param {*} content HTML表格内容
 * @returns
 */
export const extractTableData = (content) => {
  try {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const tables = tempDiv.querySelectorAll('table');

    if (!tables || tables.length === 0) {
      return null;
    }

    // 只处理第一个表格
    const table = tables[0];
    const rows = Array.from(table.querySelectorAll('tr'));

    if (rows.length === 0) {
      return null;
    }

    // 提取表头
    const headerRow = rows[0];
    const headers = Array.from(headerRow.querySelectorAll('th, td')).map((cell) => {
      const cellCopy = cell.cloneNode(true);
      const removeQuoteElements = (element) => {
        const quoteElements = element.querySelectorAll('.tag-quote');
        quoteElements.forEach((el) => el.remove());
        const children = element.children;
        for (let i = 0; i < children.length; i++) {
          removeQuoteElements(children[i]);
        }
      };
      removeQuoteElements(cellCopy);
      return cellCopy.textContent.trim().replace(/\s+/g, ' ');
    });

    // 提取数据行
    const dataRows = rows.slice(1).map((row) => {
      const cells = Array.from(row.querySelectorAll('td'));
      const rowData = {};
      cells.forEach((cell, index) => {
        const cellCopy = cell.cloneNode(true);
        const removeQuoteElements = (element) => {
          const quoteElements = element.querySelectorAll('.tag-quote');
          quoteElements.forEach((el) => el.remove());
          const children = element.children;
          for (let i = 0; i < children.length; i++) {
            removeQuoteElements(children[i]);
          }
        };
        removeQuoteElements(cellCopy);
        const value = cellCopy.textContent.trim().replace(/\s+/g, ' ');
        rowData[headers[index] || `列${index + 1}`] = value || '-';
      });
      return rowData;
    });

    return {
      columns: headers,
      data: dataRows
    };
  } catch (error) {
    console.error(i18n.global.t('aiChat.parse_table_error'), error);
    return null;
  }
};

/**
 * 解析Markdown内容，将表格内容包裹在div中，以便后续处理
 * @param {*} content Markdown内容
 * @returns
 */
export const parseTableContent = (content) => {
  const matches = content.split(/(<table[\s\S]*?>[\s\S]*?<\/table>)/gi) || [];
  return matches.map((item) => {
    if (item && typeof item === 'string' && item.includes('<table')) {
      return {
        content: `<div class="ai-table-box"><div class="ai-table">${item}</div></div>`,
        type: 'table'
      };
    } else {
      return {
        content: item,
        type: 'text'
      };
    }
  });
};

/**
 * 将Markdown内容转换为HTML内容
 *
 * @param {object} [options]
 * @param {boolean} [options.preProcess=true]
 *   是否在 markdown-it 解析前应用 `applyPreProcessors`（CJK 加粗修复、`<br>+列表标记` 修复等）。
 *   默认开启，调用方 `transformToHtml.render(text)` 会先经过预处理再进入 markdown-it。
 *   预处理函数被设计为幂等，因此 `smart_table_terminator` 内部的递归 render 调用即使
 *   被再次预处理也只是 no-op，仅有极小的扫描开销。
 * @param {boolean} [options.externalLinkNewTab=false]
 *   是否让 http/https 外链在新窗口打开（注入 target="_blank" + rel）。默认关闭，
 *   仅聊天/研究模式「正文」渲染显式开启，避免影响导出、附件、编辑器等其他调用方的链接行为。
 */
export const getTransformToHtml = ({ preProcess = true, externalLinkNewTab = false } = {}) => {
  const { markdownit, applyPreProcessors, applyKatexMath, applyExternalLinkTargetBlank } = _mdLibs || {};
  // 完全禁用引用链接的解析
  function completelyDisableReferenceLinks(md) {
    const originalParse = md.parse;
    md.parse = function (src, env) {
      // 1. 转义定义部分 如： [id]: url
      let escapedSrc = src.replace(/^(\s*)\[([^\]]+)\]:\s*(.+)$/gm, function (_, space, id, url) {
        return `${space}&#91;${id}&#93;: ${url}`;
      });

      // 2. 转义引用部分 如：[text][id] 和 [text][]
      // 过滤纯数字的情况，避免影响引用链接解析
      escapedSrc = escapedSrc
        .replace(/([^\\])\[([^\]]+)\]\[([^\]]+)\]/g, function (_, pre, text, id) {
          // 如果 text 和 id 都是纯数字，则不转义，因为这可能是引用标签 [1][2]
          if (/^\d+$/.test(text) && /^\d+$/.test(id)) {
            return `${pre}[${text}][${id}]`;
          }
          return `${pre}&#91;${text}&#93;&#91;${id}&#93;`;
        })
        .replace(/([^\\])\[([^\]]+)\]\[\]/g, function (_, pre, text) {
          if (/^\d+$/.test(text)) {
            return `${pre}[${text}][]`;
          }
          return `${pre}&#91;${text}&#93;&#91;&#93;`;
        });

      return originalParse.call(this, escapedSrc, env);
    };
  }

  const transformToHtml = applyKatexMath(
    markdownit({
      html: true, // 是否解析 html 标签
      breaks: true, // 转换 \n 为 <br/>
      langPrefix: 'language-',
      linkify: false, // 将类似 URL 的文本自动转换为链接
      typographer: true
    })
  ).use(completelyDisableReferenceLinks);

  // 正文外链在新窗口打开（http/https 外链注入 target=_blank + rel）：仅在显式开启时生效，
  // 由聊天/研究模式正文（ChatSessionTypeList → handleTransformToHtml）传入，避免影响导出/附件/编辑器等其他调用方。
  if (externalLinkNewTab) applyExternalLinkTargetBlank?.(transformToHtml);

  // 判断行内是否只在 $...$ / $$...$$ 数学公式里出现 `|`
  // 例如 `$$...|Gap_k|...$$` 或 `- $|\Delta w_{gold}| \leq 0.15$（...）`
  // 这类 LaTeX 行不应被当作 markdown 表格起始 / 表格行，避免 smart_table_terminator 误判
  const isPipeOnlyFromMath = (line) => {
    if (!line || !line.includes('|')) return false;
    // 先去掉成对的 $$...$$ 与 $...$ 数学区域
    let stripped = line.replace(/\$\$[\s\S]*?\$\$/g, '').replace(/\$[^$\n]+\$/g, '');
    // 处理多行 display math 的起/止行（仅含单侧 $$）
    stripped = stripped.replace(/\$\$/g, '');
    return !stripped.includes('|');
  };

  // 自定义表格终止规则：插入到 block 解析之后
  transformToHtml.core.ruler.after('block', 'smart_table_terminator', function (state) {
    const src = state.src;
    const lines = src.split('\n').map((line) => line.trimEnd()); // 按行拆分，去除行尾空格
    let tableStartIdx = -1;
    let tableColumnCount = 0; // 表格列数基准

    const separatorLineRegex = /^\s*[|:\-\s]+\s*$/;

    // 追踪多行 $$...$$ 块，块内行含 | 的 LaTeX（如 \begin{cases}）不应被识别为表格行
    // 与 markdown-it-mathjax3 的 math_block 解析逻辑对齐：
    //   - 行以 $$ 开头，且同行内容不以 $$ 结尾 → 进入多行块
    //   - 块内遇到以 $$ 结尾的行 → 退出块
    let insideMathBlock = false;

    // 定位表格起始位置 & 计算列数基准
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (!insideMathBlock) {
        // 行以 $$ 开头
        if (line.startsWith('$$')) {
          const afterOpen = line.slice(2).trim();
          // 同行以 $$ 结尾 → 单行块（含空公式 $$$$），不进入多行状态
          // 注：afterOpen = '' 时 endsWith('$$') 为 false，会正确进入多行块
          if (afterOpen.endsWith('$$')) {
            continue; // 单行 $$...$$，跳过整行，不作表格处理
          }
          // 否则进入多行块
          insideMathBlock = true;
          continue;
        }
      } else {
        // 在块内：遇到以 $$ 结尾的行则退出块
        if (line.endsWith('$$')) {
          insideMathBlock = false;
        }
        continue; // 块内行一律跳过，不作表格检测
      }

      const isSeparatorLineInit = separatorLineRegex.test(line) && /[-:]/.test(line);

      // 判定表格开始：非空行 + 含| + 不是分隔行 + `|` 不是全来自数学公式
      if (tableStartIdx === -1 && line && line.includes('|') && !isSeparatorLineInit && !isPipeOnlyFromMath(line)) {
        tableStartIdx = i;
        // 从分隔行计算列数（优化：兼容空列，得到真实列数）
        for (let j = i + 1; j < lines.length; j++) {
          const sepLine = lines[j].trim();

          const isValidSeparatorLine = separatorLineRegex.test(sepLine) && /[-:]/.test(sepLine);
          if (isValidSeparatorLine) {
            // 按|分割分隔行，过滤空/纯空格元素，得到真实列数
            const sepColumns = sepLine
              .split('|')
              .map((col) => col.trim())
              .filter((col) => col !== '');
            tableColumnCount = sepColumns.length;
            // 兜底处理：避免列数为0
            if (tableColumnCount === 0) {
              tableColumnCount = (sepLine.match(/\|/g) || []).length - 1;
            }
            break;
          }
        }
      }

      // 表格状态下，校验每行是否符合规则（优化版：兼容空列和流式数据）
      if (tableStartIdx !== -1 && tableColumnCount > 0) {
        let currentLine = lines[i].trim();
        // 空行：不终止表格，跳过
        if (!currentLine) continue;

        // 如果当前行不含|，或 `|` 全部来自 LaTeX 数学，视为非表格行，触发终止
        if (!currentLine.includes('|') || isPipeOnlyFromMath(currentLine)) {
          const tableContent = lines.slice(0, i).join('\n');
          const nonTableContent = lines.slice(i).join('\n').trimStart();
          const tableHtml = transformToHtml.render(tableContent);
          const nonTableHtml = transformToHtml.render(nonTableContent);
          state.tokens = [{ type: 'html_block', content: tableHtml + nonTableHtml, block: true }];
          break;
        }

        // 格式化当前行，兼容空列（|| → |  |，统一格式）
        currentLine = currentLine.replace(/\|\|/g, '| |');

        // 空列补全（针对流式不完整数据，自动补全缺失列占位）
        const isSeparatorLineTemp = separatorLineRegex.test(currentLine) && /[-:]/.test(currentLine);
        if (!isSeparatorLineTemp && tableColumnCount > 0) {
          const currentColumns = currentLine.split('|').map((col) => col.trim());
          const validCurrentColumns = currentColumns.filter((col) => col !== '').length;
          const needFillColumns = tableColumnCount - validCurrentColumns;
          if (needFillColumns > 0) {
            const fillStr = '|  '.repeat(needFillColumns);
            currentLine = currentLine.endsWith('|') ? currentLine + fillStr.slice(1) : currentLine + fillStr;
            lines[i] = currentLine; // 写回lines数组，确保后续解析正确
          }
        }

        // 重新获取分隔行状态和有效数据
        const isSeparatorLine = separatorLineRegex.test(currentLine) && /[-:]/.test(currentLine);
        const lineColumns = currentLine
          .split('|')
          .map((col) => col.trim())
          .filter((col) => col !== '');
        const validColumnCount = lineColumns.length;
        const pipeCount = (currentLine.match(/\|/g) || []).length;
        const isSinglePipeText = pipeCount === 1 && !currentLine.startsWith('|');

        // 重构终止条件（避免空列误判）
        const isTooFewColumns = validColumnCount < Math.max(1, Math.floor(tableColumnCount / 2));
        const hasNoTableStructure = !currentLine.includes('||') && pipeCount < 2;
        const isNonTableLine = !isSeparatorLine && isTooFewColumns && !isSinglePipeText && hasNoTableStructure;

        if (isNonTableLine) {
          // 拆分表格内容和非表格内容，分别解析
          const tableContent = lines.slice(0, i).join('\n');
          const nonTableContent = lines.slice(i).join('\n').trimStart();

          // 重新渲染并替换 tokens
          const tableHtml = transformToHtml.render(tableContent);
          const nonTableHtml = transformToHtml.render(nonTableContent);
          state.tokens = [
            {
              type: 'html_block',
              content: tableHtml + nonTableHtml,
              block: true
            }
          ];
          break;
        }
      }
    }
  });

  // 在进入 markdown-it 解析之前统一执行字符串级预处理（默认开启）。
  // 与 `markdownPreview/index.vue` 的 `preProcess` 选项语义保持一致，
  // 业务侧通过 `registerPreProcessor` 扩展的规则在这里也会生效。
  if (preProcess) {
    const originalRender = transformToHtml.render.bind(transformToHtml);
    transformToHtml.render = (src, env) => originalRender(applyPreProcessors(String(src ?? '')), env);
  }

  return transformToHtml;
};

/**
 * 在新标签页或指定窗口中打开链接。
 * @param {string} url - 目标地址
 * @param {object} [options]
 * @param {Window} [options.windowTarget] - 指定窗口，设置其 location.href
 * @param {boolean} [options.openExternalInElectron=false] - Electron 环境下是否使用系统浏览器打开
 */
export const openInNewTab = (url, options = {}) => {
  const { windowTarget, openExternalInElectron = false } = options;
  // Electron 下可选走系统默认浏览器
  if (openExternalInElectron && isElectron) {
    electron.openExternal(url);
    return;
  }
  if (windowTarget) {
    windowTarget.location.href = url;
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};
